// client.ts — Road Builder UI: rendering, interaction, panel updates,
// daily puzzle fetch, leaderboard submission. Imports authoritative
// terrain and scoring logic from ./shared/.
//
// This is deliberately one file. It is long, so the section banners below are
// the navigation: search for the banner text to jump. Order is roughly
// "data in → drawn → interacted with → sent back out".
//
//   CONTROL-POINT BUDGET   bridge to the CPBudget module in index.html
//   STATE                  module-level mutable state, canvas, view transform
//   ROAD MODEL             anchors/handles, cubic maths, hit testing
//   ARC LENGTH TABLES      sub-cubic extraction for the multi-colour render
//   HYPSOMETRIC RELIEF     terrain raster, contours, rivers, lakes
//   DEBUG GRADE VIZ        \
//   PROPOSED ROAD           |  the draw layer, roughly back-to-front
//   HANDLES & TOWNS         |
//   DEBUG OVERLAYS          |
//   TOWN LAYOUT             |
//   CAR ANIMATION           |
//   MAIN DRAW & RESIZE     /   compositor, static-layer cache, rAF loop
//   INTERACTION            pointer/keyboard/pinch, drag state machine
//   PANEL UPDATES          the readouts beside the map
//   ARCHIVE MODE           ?date= loading, day stepping
//   API                    puzzle fetch, score submit, leaderboard poll
//   TARGET (PAR)           par state, Pareto assessment, target rendering
//   FRONTIER STREAK        client-local achievement counter
//   BUTTONS & INIT         DOM wiring; the entry point is at the bottom
//   ELEVATION PROFILE      the strip below the map

// UI modules, formerly inline <script> blocks in index.html. Imported for
// their side effects only — each is an IIFE that wires its own DOM.
//
// ORDER IS LOAD-BEARING for the first two. As classic scripts they ran during
// parse, before this deferred module; the code below reads window.I18N and
// window.CPBudget and degrades to "no helper" when either is missing. An ES
// module's imports are evaluated before the importing module's body, so
// listing them here reproduces that guarantee. Don't reorder them below the
// ./shared imports, and don't let a tidier drop them as "unused".
import "./ui/i18n.js";
import "./ui/cpBudget.js";
import "./ui/music.js";
import "./ui/coach.js";
import "./ui/surveyForm.js";
import "./ui/mobileSubmit.js";

import {
  heightAt, MAP_W, MAP_H, WATER_THRESHOLD, HEIGHT_SCALE_KM, WU_TO_KM,
  decodeHeightmapU16,
} from "./shared/terrain.js";
import type { Puzzle, TerrainParams, Skeleton, River, Lake } from "./shared/terrain.js";
import { analyzeRoad, cubicPoint, MAX_GRADE } from "./shared/scoring.js";
import type { Road, Pt, Kind, Analysis } from "./shared/scoring.js";
import { PAR_STRINGS, resolveLocale } from "./strings.js";
import type { ParStrings } from "./strings.js";

// =============================================================
// CONTROL-POINT BUDGET
// =============================================================
// Defined by an inline classic script in index.html, which therefore runs
// before this deferred module. The rule (cap, sandbox mode, submit gate,
// header readout) lives entirely over there; this file only reports the
// count and asks permission before adding anchors.
//
// "Control points" means INTERIOR anchors — road.anchors.length minus the
// two town endpoints, which are fixed, undraggable, and deliberately not
// drawn as handles. Counting them would show a number two higher than the
// dots the player can see.
//
// The Window shape for CPBudget and I18N is declared once, in ui/globals.d.ts,
// beside the modules that publish them. This file used to carry its own copy;
// the two had already drifted (the local I18N type was missing setLocale and
// fromCountry), which is exactly the failure mode of declaring a global twice.
type CPBudgetApi = NonNullable<Window["CPBudget"]>;
// Read through a function rather than captured once, so a missing or
// late-loading module degrades to "no budget" instead of a TypeError.
function cp(): CPBudgetApi | undefined { return window.CPBudget; }
/** Interior anchor count — the number the player sees and the rule counts. */
function controlPointCount(): number { return Math.max(0, road.anchors.length - 2); }
/** True when an anchor may be added. Permissive if the module is absent. */
function cpCanAdd(): boolean { const m = cp(); return !m || m.canAdd(); }

/** v1 wire format: base64 of little-endian Float32 bytes. */
function decodeF32B64(b64: string): Float32Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Float32Array(bytes.buffer);
}


// =============================================================
// STATE
// =============================================================

let puzzle: Puzzle | null = null;
let dateStr = "";
let road: Road = { anchors: [], handles: [] };
let lastAnalysis: Analysis | null = null;
let DEBUG_GRADE_VIZ = false;

// --- Page query params (developer-only) ----------------------------------
// Read once, here, rather than at each use site: they're fixed for the life
// of the page and scattered location.search parsing is how you end up with
// two spellings of the same flag.
const pageParams = new URLSearchParams(location.search);
/** ?debug=true — ask the API for the per-cell drainage/spillway payload. */
const DEBUG_PAYLOAD = pageParams.get("debug") === "true";
/** ?date=YYYY-MM-DD — load an archived day instead of today's puzzle. */
const REQUESTED_DATE = pageParams.get("date");
/**
 * Whether the server confirmed it served something other than today.
 *
 * Set from the response, never inferred from `REQUESTED_DATE !== null` or
 * from comparing dateStr to a locally-computed date: the client's local day
 * and the server's UTC day differ for part of every day, so a client that
 * judged for itself would put live puzzles into archive mode each evening
 * east of UTC.
 */
let archiveMode = false;
/** The server's own "today", captured on load. Used to clamp date stepping. */
let serverToday = "";

// --- Debug overlays (developer-only) -------------------------------------
// Toggle with keyboard shortcuts:
//   S — toggle ridge skeleton overlay (peaks + edges + trunk)
//   E — toggle elevation probe mode (click anywhere to drop an elevation label)
//   C — clear all elevation probes
//   D — toggle per-cell drainage labels (requires page loaded with ?debug=true)
//   G — toggle the per-cell debug grid (half-integer cell-boundary lines)
//   F — snap the last-touched anchor's handles perpendicular to the fall line
//   [ / ] — step back / forward one puzzle date (archive mode; see below)
//
// Modifier during a handle drag:
//   Shift — freeze the handle's angle; the drag then changes only its length
let DEBUG_SKELETON = false;
let DEBUG_ELEVATION = false;
let DEBUG_DRAINAGE = false;
let DEBUG_GRID = false;
let skeleton: Skeleton | null = null;
let rivers: River[] = [];
let lakes: Lake[] = [];
// Per-cell drainage values above the server-side threshold. Populated only
// when the page is loaded with ?debug=true (the server omits this otherwise).
let drainageCells: Array<{ x: number; y: number; d: number }> = [];
// Lake spillway cells (red) and their discoverer cells (orange): the cell
// water overtops to exit a lake, and the lake-perimeter cell whose flood-
// fill neighbor-scan first reached that spillway. One of each per
// outflowing lake. Same ?debug=true gating as drainageCells.
let spillwayCells: Array<{ x: number; y: number }> = [];
let discovererCells: Array<{ x: number; y: number }> = [];
// World-space click positions whose elevation we want labeled on the map.
let elevationProbes: Pt[] = [];

const canvas = document.getElementById("map") as HTMLCanvasElement;
const mainCtx = canvas.getContext("2d")!;
// `ctx` is the *current* draw target. Normally points at the on-screen
// canvas; temporarily swapped to the static-layer canvas during compositing.
let ctx: CanvasRenderingContext2D = mainCtx;

// --- Static-layer cache --------------------------------------------------
// Everything that doesn't change frame-to-frame (relief, grid, contours,
// towns, town labels, existing road) is composited into a single
// offscreen canvas sized to the on-screen canvas. The per-frame redraw
// blits this once and then draws only the dynamic layers (road, cars,
// handles) on top.
//
// Invalidated by: puzzle load, resize, pan, zoom.
// (DEBUG_GRADE_VIZ only affects the road, which is in the dynamic layer.)
let staticCanvas: HTMLCanvasElement | null = null;
let staticDirty = true;
function invalidateStatic() { staticDirty = true; }

// During fast zoom/pan, recomputing the static composite every frame is
// expensive — contours in particular involve thousands of toScreen() calls
// and many separate stroke()s. We defer contour drawing while the user is
// navigating: a `navigating` flag short-circuits drawContours(), and a
// trailing-edge debounce timer schedules a full rebuild ~80ms after the
// last navigation event. The relief, grid, towns, etc. still draw at full
// quality during the zoom — only contours pop back in after settle.
let navigating = false;
let navigateSettleTimer: number | null = null;
const NAVIGATE_SETTLE_MS = 80;
function markNavigating() {
  navigating = true;
  if (navigateSettleTimer != null) clearTimeout(navigateSettleTimer);
  navigateSettleTimer = window.setTimeout(() => {
    navigating = false;
    navigateSettleTimer = null;
    invalidateStatic();
    scheduleRender();
  }, NAVIGATE_SETTLE_MS);
  invalidateStatic();
}
// Force-end any pending navigation debounce. Used by discrete actions
// (recenter, resize) that want full-quality rendering immediately.
function commitNavigating() {
  if (navigateSettleTimer != null) {
    clearTimeout(navigateSettleTimer);
    navigateSettleTimer = null;
  }
  navigating = false;
}

// --- Cached contour geometry --------------------------------------------
// Contour lines are computed in *world* coordinates once per puzzle via
// marching squares. Drawing then just walks the cached segments through
// toScreen() — no heightAt() calls, no marching squares, per redraw.
type ContourLevel = {
  level: number;
  isMajor: boolean;
  // Flat segment list: [x1,y1,x2,y2, x1,y1,x2,y2, ...] in world coordinates.
  // Float32Array is much faster to iterate than an array of {a,b} objects.
  segments: Float32Array;
};
let cachedContours: ContourLevel[] | null = null;


// view transform
let cssW = 800, cssH = 500;
let fitScale = 1, fitOffsetX = 0, fitOffsetY = 0;
let viewScale = 1, viewPanX = 0, viewPanY = 0;
const MIN_ZOOM = 0.6;
/**
 * Zoom ceiling. Raised from 8 so handles can be separated at the scale where
 * they bunch up.
 *
 * What degrades past ~8x is bitmap-backed scenery, not geometry: the
 * hypsometric relief is rasterised once at RELIEF_CELLS_PER_UNIT (2 samples
 * per world unit) and upscaled by the browser from there. Contours, the road, handles
 * and towns are all drawn from world coordinates every frame and stay sharp
 * at any zoom. 24x is about where the relief blur becomes the dominant thing
 * on screen; raising RELIEF_CELLS_PER_UNIT would push that further out at a
 * quadratic cost in the one-off raster.
 */
const MAX_ZOOM = 24;

function curScale(): number { return fitScale * viewScale; }

/**
 * How much empty space beyond the map edge may be pulled into view, as a
 * fraction of the viewport, ON TOP OF the letterboxing the fit view already
 * shows. Raise for a looser leash.
 *
 * Measured on top of the letterbox rather than against the raw viewport, and
 * that detail is the whole design. At fit zoom the map is centred inside a
 * 30px margin, so on a 900x600 viewport there is already ~90px of empty space
 * above and below it. A flat "allow 12% of the viewport" rule would call the
 * DEFAULT VIEW a violation, and the directional rule below would then refuse
 * to pan in either direction — the map would appear frozen at rest. Anchoring
 * to fitOffset makes the fit view exactly neutral, with this fraction as
 * symmetric slack around it.
 */
const OOB_MARGIN_FRAC = 0.10;

/**
 * Pan limits for the current zoom.
 *
 * `max` is the pan at which the left/top edge of the map has been dragged as
 * far inward as allowed; `min` is the same for right/bottom. `mid` centres the
 * map, and is the fallback when the two cross — see clampPanTo().
 */
function panLimits() {
  const mapW = MAP_W * fitScale * viewScale;
  const mapH = MAP_H * fitScale * viewScale;
  const oobX = fitOffsetX + cssW * OOB_MARGIN_FRAC;
  const oobY = fitOffsetY + cssH * OOB_MARGIN_FRAC;
  return {
    maxX: oobX - fitOffsetX,
    minX: cssW - oobX - fitOffsetX - mapW,
    midX: (cssW - mapW) / 2 - fitOffsetX,
    maxY: oobY - fitOffsetY,
    minY: cssH - oobY - fitOffsetY - mapH,
    midY: (cssH - mapH) / 2 - fitOffsetY,
  };
}

/**
 * The pan accepted by the previous clamp, per axis.
 *
 * The directional rule needs to know which way the view is travelling, and
 * the callers mutate viewPanX/Y in place before clamping, so the old value
 * has to be remembered here rather than passed in.
 */
let panPrevX = 0, panPrevY = 0;

/**
 * Block movement that reveals more out-of-bounds than allowed IN THAT
 * DIRECTION, while always permitting movement back toward the map.
 *
 * Zooming out shrinks the map on screen, and below roughly fit scale it no
 * longer spans the viewport minus both margins — at which point `lo` rises
 * above `hi` and there is no pan that satisfies both edges at once. A plain
 * two-sided clamp inverts there and snaps the view to a corner. This rule
 * sidesteps that by never asking whether the POSITION is legal, only whether
 * the MOVE makes things worse.
 *
 * WHEN BOTH EDGES ARE ALREADY OVER (the `lo > hi` case), refusing every move
 * would freeze the view wherever it happened to land, which is worse than the
 * thing being prevented. So movement is permitted in the direction that closes
 * the LARGER of the two margins, at the cost of the smaller one.
 *
 * That reduces to "move toward `mid`". The two margins are equal only at
 * `mid`, and whichever side you are moving away from is always the larger one,
 * so "close the bigger gap" and "reduce the distance to centre" are the same
 * instruction. Travel stops AT `mid` rather than sailing past it, since going
 * further would start growing the gap that was just closed.
 *
 * `mid` sits inside [lo, hi] whenever that interval is non-empty, so the two
 * branches meet continuously — no jump as zoom crosses the threshold.
 */
function clampAxisDirectional(
  next: number, prev: number, lo: number, hi: number, mid: number,
): number {
  if (lo <= hi) {
    // Past the upper bound and still climbing: hold at whichever is nearer the
    // map — `prev` when it was already out, `hi` when it was not.
    if (next > hi && next > prev) return Math.max(prev, hi);
    if (next < lo && next < prev) return Math.min(prev, lo);
    return next;
  }
  // Degenerate: every pan is over on one side or the other.
  if (next > prev) {
    // Moving up closes the far (right/bottom) margin. Only worth allowing
    // while that margin is the larger one, i.e. while below centre.
    return prev >= mid ? prev : Math.min(next, mid);
  }
  if (next < prev) {
    return prev <= mid ? prev : Math.max(next, mid);
  }
  return next;
}

/** Interactive pan, pinch and wheel-zoom. Directional, never inverts. */
function clampPan() {
  const L = panLimits();
  viewPanX = clampAxisDirectional(viewPanX, panPrevX, L.minX, L.maxX, L.midX);
  viewPanY = clampAxisDirectional(viewPanY, panPrevY, L.minY, L.maxY, L.midY);
  panPrevX = viewPanX;
  panPrevY = viewPanY;
}

/**
 * Absolute clamp, for jumps rather than drags: recenter, the profile
 * double-click jump, and resize.
 *
 * A jump has no direction to reason about — the view teleports — so the
 * directional rule would judge it against wherever the player happened to be
 * and could refuse a legitimate destination. This clamps into the allowed
 * band instead, falling back to centred when the band has collapsed. It also
 * reseeds the baseline, so the next interactive pan measures from the new
 * position rather than the abandoned one.
 */
function clampPanTo() {
  const L = panLimits();
  viewPanX = L.minX > L.maxX ? L.midX : Math.max(L.minX, Math.min(L.maxX, viewPanX));
  viewPanY = L.minY > L.maxY ? L.midY : Math.max(L.minY, Math.min(L.maxY, viewPanY));
  panPrevX = viewPanX;
  panPrevY = viewPanY;
}

function toScreen(p: Pt): Pt {
  return {
    x: fitOffsetX + p.x * fitScale * viewScale + viewPanX,
    y: fitOffsetY + p.y * fitScale * viewScale + viewPanY,
  };
}
function toWorld(sx: number, sy: number): Pt {
  return {
    x: (sx - fitOffsetX - viewPanX) / (fitScale * viewScale),
    y: (sy - fitOffsetY - viewPanY) / (fitScale * viewScale),
  };
}
function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

// =============================================================
// ROAD MODEL
// =============================================================

function defaultRoad(p: Puzzle): Road {
  const a = p.towns[0], b = p.towns[1];
  return {
    anchors: [{ x: a.x, y: a.y }, { x: b.x, y: b.y }],
    handles: [
      { in: null, out: { x: a.x + 50, y: a.y - 16 } },
      { in: { x: b.x - 50, y: b.y + 16 }, out: null },
    ],
  };
}

function addSegment() {
  // Was a hardcoded `road.anchors.length >= 8`. That cap was invisible to
  // the player, applied only to this button (click-to-insert on the canvas
  // was unlimited), and was *tighter* than the new 10-control-point rule —
  // so leaving it in would have silently overridden the whole feature.
  if (!cpCanAdd()) { cp()?.refuse(); return; }
  let maxLen = 0, idx = 0;
  for (let i = 0; i < road.anchors.length - 1; i++) {
    const a = road.anchors[i], b = road.anchors[i + 1];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len > maxLen) { maxLen = len; idx = i; }
  }
  const a = road.anchors[idx], b = road.anchors[idx + 1];
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const dx = b.x - a.x, dy = b.y - a.y;
  road.anchors.splice(idx + 1, 0, mid);
  road.handles.splice(idx + 1, 0, {
    in: { x: mid.x - dx * 0.15, y: mid.y - dy * 0.15 },
    out: { x: mid.x + dx * 0.15, y: mid.y + dy * 0.15 },
  });
}

function removeSegmentMid() {
  if (road.anchors.length <= 2) return;
  const idx = Math.floor(road.anchors.length / 2);
  road.anchors.splice(idx, 1);
  road.handles.splice(idx, 1);
}

// De Casteljau split — exact insertion of an anchor at parameter t on segment segIndex
function splitCubicAt(p0: Pt, c1: Pt, c2: Pt, p1: Pt, t: number) {
  const lerp = (a: Pt, b: Pt, k: number) => ({ x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k });
  const q0 = lerp(p0, c1, t), q1 = lerp(c1, c2, t), q2 = lerp(c2, p1, t);
  const r0 = lerp(q0, q1, t), r1 = lerp(q1, q2, t);
  const s = lerp(r0, r1, t);
  return { newAnchor: s, p0OutNew: q0, leftOut: r0, rightIn: r1, p1InNew: q2 };
}
function insertAnchorOnSegment(segIndex: number, t: number): number {
  const p0 = road.anchors[segIndex], p1 = road.anchors[segIndex + 1];
  const h0 = road.handles[segIndex], h1 = road.handles[segIndex + 1];
  const split = splitCubicAt(p0, h0.out!, h1.in!, p1, t);
  h0.out = split.p0OutNew;
  h1.in = split.p1InNew;
  road.anchors.splice(segIndex + 1, 0, split.newAnchor);
  road.handles.splice(segIndex + 1, 0, { in: split.leftOut, out: split.rightIn });
  return segIndex + 1;
}

// Cubic Bézier first and second derivatives at parameter t. Defined as
// regular functions next to closestPointOnRoad rather than in scoring.ts
// because they're only used by the cursor-hover hit test.
function cubicDeriv(p0: Pt, c1: Pt, c2: Pt, p1: Pt, t: number): Pt {
  const u = 1 - t;
  // B'(t) = 3(1-t)² (C1-P0) + 6(1-t)t (C2-C1) + 3t² (P1-C2)
  const k0 = 3 * u * u, k1 = 6 * u * t, k2 = 3 * t * t;
  return {
    x: k0 * (c1.x - p0.x) + k1 * (c2.x - c1.x) + k2 * (p1.x - c2.x),
    y: k0 * (c1.y - p0.y) + k1 * (c2.y - c1.y) + k2 * (p1.y - c2.y),
  };
}
function cubicDeriv2(p0: Pt, c1: Pt, c2: Pt, p1: Pt, t: number): Pt {
  // B''(t) = 6(1-t)(C2 - 2C1 + P0) + 6t(P1 - 2C2 + C1)
  const u = 1 - t;
  return {
    x: 6 * u * (c2.x - 2 * c1.x + p0.x) + 6 * t * (p1.x - 2 * c2.x + c1.x),
    y: 6 * u * (c2.y - 2 * c1.y + p0.y) + 6 * t * (p1.y - 2 * c2.y + c1.y),
  };
}

// Closest point on the road to `query`. Uses a coarse sweep to seed the
// nearest Bézier segment and an initial t, then Newton-refines to the true
// minimum. The condition for the nearest point on a curve is that the
// vector from query to point is perpendicular to the curve tangent —
// i.e., f(t) = (B(t) − Q) · B'(t) = 0. Newton iterates on f using
//   f'(t) = B'(t)·B'(t) + (B(t) − Q)·B''(t).
// Without refinement, the sample-only version reports the distance to the
// nearest sample, which leaves cold spots between samples (visible as a
// regular stripe pattern of valid/invalid add-anchor cursor zones along
// the road, more pronounced at zoom-in where the world-space sample
// spacing covers more screen pixels).
function closestPointOnRoad(query: Pt): { segIndex: number; t: number; point: Pt; dist: number } | null {
  let best: { segIndex: number; t: number; point: Pt; dist: number } | null = null;
  // Coarse sweep: 20 samples per segment is plenty when Newton does the
  // heavy lifting. Includes endpoints (s = 0..SAMPLES) so an endpoint can
  // be the seed; the clamp-to-[0,1] in refinement handles the rest.
  const SAMPLES = 20;
  for (let i = 0; i < road.anchors.length - 1; i++) {
    const p0 = road.anchors[i], p1 = road.anchors[i + 1];
    const c1 = road.handles[i].out, c2 = road.handles[i + 1].in;
    if (!c1 || !c2) continue;

    // 1) Coarse: find the best sample t on this segment.
    let segBestT = 0;
    let segBestDist = Infinity;
    let segBestPt: Pt = p0;
    for (let s = 0; s <= SAMPLES; s++) {
      const t = s / SAMPLES;
      const p = cubicPoint(p0, c1, c2, p1, t);
      const d = Math.hypot(p.x - query.x, p.y - query.y);
      if (d < segBestDist) { segBestDist = d; segBestT = t; segBestPt = p; }
    }

    // 2) Refine: Newton iteration on f(t) = (B(t) - Q) · B'(t).
    // 4 iterations is well-converged for our seed quality; we break early
    // if the step is already negligible. Clamp t to [0,1] after each step.
    let t = segBestT;
    let pt = segBestPt;
    for (let iter = 0; iter < 4; iter++) {
      const d1 = cubicDeriv(p0, c1, c2, p1, t);
      const d2 = cubicDeriv2(p0, c1, c2, p1, t);
      const dx = pt.x - query.x, dy = pt.y - query.y;
      const f = dx * d1.x + dy * d1.y;                       // (B-Q)·B'
      const fp = d1.x * d1.x + d1.y * d1.y + dx * d2.x + dy * d2.y;  // |B'|² + (B-Q)·B''
      if (Math.abs(fp) < 1e-12) break;                       // degenerate
      let tNext = t - f / fp;
      if (tNext < 0) tNext = 0;
      else if (tNext > 1) tNext = 1;
      if (Math.abs(tNext - t) < 1e-6) { t = tNext; break; }  // converged
      t = tNext;
      pt = cubicPoint(p0, c1, c2, p1, t);
    }
    const dx = pt.x - query.x, dy = pt.y - query.y;
    const dist = Math.hypot(dx, dy);

    if (!best || dist < best.dist) best = { segIndex: i, t, point: pt, dist };
  }
  return best;
}

// =============================================================
// ARC LENGTH TABLES & SUB-CUBIC EXTRACTION (for multi-color render)
// =============================================================

function buildArcTables() {
  const SAMPLES_PER_SEG = 64;
  // Tables use km, matching Sample.s convention. Coordinate deltas come
  // out in world units; we apply WU_TO_KM once per accumulation.
  const tables: Array<Array<{ t: number; s: number }> | null> = [];
  const cumLen = [0];
  for (let i = 0; i < road.anchors.length - 1; i++) {
    const p0 = road.anchors[i], p1 = road.anchors[i + 1];
    const c1 = road.handles[i].out, c2 = road.handles[i + 1].in;
    if (!c1 || !c2) { tables.push(null); cumLen.push(cumLen[i]); continue; }
    const table = [{ t: 0, s: 0 }];
    let prev = cubicPoint(p0, c1, c2, p1, 0);
    let segLen = 0;
    for (let k = 1; k <= SAMPLES_PER_SEG; k++) {
      const t = k / SAMPLES_PER_SEG;
      const p = cubicPoint(p0, c1, c2, p1, t);
      segLen += Math.hypot(p.x - prev.x, p.y - prev.y) * WU_TO_KM;
      table.push({ t, s: segLen });
      prev = p;
    }
    tables.push(table);
    cumLen.push(cumLen[i] + segLen);
  }
  return { tables, cumLen };
}

function arclengthToSegmentT(sQuery: number, arcInfo: ReturnType<typeof buildArcTables>) {
  const { tables, cumLen } = arcInfo;
  const totalLen = cumLen[cumLen.length - 1];
  if (sQuery <= 0) return { segIndex: 0, t: 0 };
  if (sQuery >= totalLen) {
    for (let i = tables.length - 1; i >= 0; i--) {
      if (tables[i]) return { segIndex: i, t: 1 };
    }
    return null;
  }
  let segIndex = 0;
  for (let i = 0; i < cumLen.length - 1; i++) {
    if (sQuery >= cumLen[i] && sQuery < cumLen[i + 1]) { segIndex = i; break; }
  }
  if (!tables[segIndex]) return null;
  const sLocal = sQuery - cumLen[segIndex];
  const table = tables[segIndex]!;
  for (let k = 1; k < table.length; k++) {
    if (table[k].s >= sLocal) {
      const s0 = table[k - 1].s, s1 = table[k].s;
      const t0 = table[k - 1].t, t1 = table[k].t;
      const f = (s1 - s0) > 0 ? (sLocal - s0) / (s1 - s0) : 0;
      return { segIndex, t: t0 + (t1 - t0) * f };
    }
  }
  return { segIndex, t: 1 };
}

function extractSubCubic(p0: Pt, c1: Pt, c2: Pt, p1: Pt, tA: number, tB: number) {
  const lerp = (a: Pt, b: Pt, k: number) => ({ x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k });
  function left(p0: Pt, c1: Pt, c2: Pt, p1: Pt, t: number) {
    const q0 = lerp(p0, c1, t), q1 = lerp(c1, c2, t), q2 = lerp(c2, p1, t);
    const r0 = lerp(q0, q1, t), r1 = lerp(q1, q2, t);
    const s = lerp(r0, r1, t);
    return { p0, c1: q0, c2: r0, p1: s };
  }
  function right(p0: Pt, c1: Pt, c2: Pt, p1: Pt, t: number) {
    const q0 = lerp(p0, c1, t), q1 = lerp(c1, c2, t), q2 = lerp(c2, p1, t);
    const r0 = lerp(q0, q1, t), r1 = lerp(q1, q2, t);
    const s = lerp(r0, r1, t);
    return { p0: s, c1: r1, c2: q2, p1 };
  }
  if (tB <= 1e-9) return { p0, c1: p0, c2: p0, p1: p0 };
  const L = left(p0, c1, c2, p1, tB);
  const tR = tA / tB;
  if (tR >= 1) return { p0: L.p1, c1: L.p1, c2: L.p1, p1: L.p1 };
  return right(L.p0, L.c1, L.c2, L.p1, tR);
}

function getCubicSubpath(sStart: number, sEnd: number, arcInfo: ReturnType<typeof buildArcTables>) {
  if (sEnd <= sStart) return [];
  const a = arclengthToSegmentT(sStart, arcInfo);
  const b = arclengthToSegmentT(sEnd, arcInfo);
  if (!a || !b) return [];
  const full = (i: number) => ({
    p0: road.anchors[i], c1: road.handles[i].out!,
    c2: road.handles[i + 1].in!, p1: road.anchors[i + 1],
  });
  const out = [];
  if (a.segIndex === b.segIndex) {
    const f = full(a.segIndex);
    out.push(extractSubCubic(f.p0, f.c1, f.c2, f.p1, a.t, b.t));
  } else {
    const fa = full(a.segIndex);
    out.push(extractSubCubic(fa.p0, fa.c1, fa.c2, fa.p1, a.t, 1));
    for (let i = a.segIndex + 1; i < b.segIndex; i++) out.push(full(i));
    const fb = full(b.segIndex);
    out.push(extractSubCubic(fb.p0, fb.c1, fb.c2, fb.p1, 0, b.t));
  }
  return out;
}

function strokeCubicSubpath(cubics: ReturnType<typeof getCubicSubpath>) {
  if (cubics.length === 0) return;
  ctx.beginPath();
  const first = toScreen(cubics[0].p0);
  ctx.moveTo(first.x, first.y);
  for (const c of cubics) {
    const sc1 = toScreen(c.c1), sc2 = toScreen(c.c2), sp1 = toScreen(c.p1);
    ctx.bezierCurveTo(sc1.x, sc1.y, sc2.x, sc2.y, sp1.x, sp1.y);
  }
  ctx.stroke();
}

function strokeSmoothBezier() {
  ctx.beginPath();
  for (let i = 0; i < road.anchors.length - 1; i++) {
    const p0 = road.anchors[i], p1 = road.anchors[i + 1];
    const c1 = road.handles[i].out, c2 = road.handles[i + 1].in;
    if (!c1 || !c2) continue;
    const sp0 = toScreen(p0), sc1 = toScreen(c1), sc2 = toScreen(c2), sp1 = toScreen(p1);
    if (i === 0) ctx.moveTo(sp0.x, sp0.y);
    ctx.bezierCurveTo(sc1.x, sc1.y, sc2.x, sc2.y, sp1.x, sp1.y);
  }
  ctx.stroke();
}

function findStructureRuns(kinds: Kind[]) {
  const runs: Array<{ kind: Kind; startIdx: number; endIdx: number }> = [];
  if (!kinds || kinds.length === 0) return runs;
  let runStart = 0, runKind = kinds[0];
  for (let i = 1; i < kinds.length; i++) {
    if (kinds[i] !== runKind) {
      runs.push({ kind: runKind, startIdx: runStart, endIdx: i });
      runStart = i; runKind = kinds[i];
    }
  }
  runs.push({ kind: runKind, startIdx: runStart, endIdx: kinds.length });
  return runs;
}

// =============================================================
// HYPSOMETRIC RELIEF
// =============================================================

type Stop = { at: number; r: number; g: number; b: number };
const HYPSO_BASE_STOPS: Stop[] = [
  { at: 0.00, r: 60,  g: 100, b: 150 },
  { at: 0.18, r: 130, g: 170, b: 200 },
  { at: 0.22, r: 168, g: 195, b: 145 },
  { at: 0.35, r: 200, g: 200, b: 130 },
  { at: 0.50, r: 210, g: 180, b: 110 },
  { at: 0.65, r: 180, g: 140, b: 90 },
  { at: 0.80, r: 140, g: 110, b: 80 },
  { at: 0.92, r: 130, g: 125, b: 115 },
  { at: 1.00, r: 220, g: 215, b: 210 },
];
const SNOW_PERCENTILE = 0.95;
const SNOW_FLOOR = 1.0;
const GREY_PERCENTILE = 0.90;

let HYPSO_STOPS: Stop[] = HYPSO_BASE_STOPS;

function buildHypsoStops(params: TerrainParams): Stop[] {
  const SAMPLES = 64;
  const elevs: number[] = [];
  for (let j = 0; j < SAMPLES; j++) {
    for (let i = 0; i < SAMPLES; i++) {
      const x = (i + 0.5) / SAMPLES * MAP_W;
      const y = (j + 0.5) / SAMPLES * MAP_H;
      elevs.push(heightAt(x, y, params));
    }
  }
  elevs.sort((a, b) => a - b);
  const snowAnchor = Math.max(SNOW_FLOOR, elevs[Math.floor(elevs.length * SNOW_PERCENTILE)]);
  const greyAnchor = Math.max(SNOW_FLOOR - 0.07, elevs[Math.floor(elevs.length * GREY_PERCENTILE)]);
  const stops = HYPSO_BASE_STOPS.map(s => ({ ...s }));
  stops[7].at = greyAnchor;
  stops[8].at = snowAnchor;
  return stops;
}

function sampleHypso(h: number) {
  h = Math.max(0, Math.min(1, h));
  for (let i = 1; i < HYPSO_STOPS.length; i++) {
    if (h <= HYPSO_STOPS[i].at) {
      const a = HYPSO_STOPS[i - 1], b = HYPSO_STOPS[i];
      const t = (h - a.at) / (b.at - a.at);
      return {
        r: Math.round(a.r + (b.r - a.r) * t),
        g: Math.round(a.g + (b.g - a.g) * t),
        b: Math.round(a.b + (b.b - a.b) * t),
      };
    }
  }
  const last = HYPSO_STOPS[HYPSO_STOPS.length - 1];
  return { r: last.r, g: last.g, b: last.b };
}

// Relief canvas: pixels per world unit. Halved when MAP_W/MAP_H were
// aligned to the heightmap (which doubled the world-unit count) so the
// canvas size stays roughly where it was — 400×240 was visually fine.
const RELIEF_CELLS_PER_UNIT = 2;
let reliefCanvas: HTMLCanvasElement | null = null;

function buildReliefCanvas(params: TerrainParams): HTMLCanvasElement {
  const W = MAP_W * RELIEF_CELLS_PER_UNIT;
  const H = MAP_H * RELIEF_CELLS_PER_UNIT;
  const off = document.createElement("canvas");
  off.width = W; off.height = H;
  const oc = off.getContext("2d")!;
  const img = oc.createImageData(W, H);
  const data = img.data;
  for (let j = 0; j < H; j++) {
    for (let i = 0; i < W; i++) {
      const x = (i + 0.5) / RELIEF_CELLS_PER_UNIT;
      const y = (j + 0.5) / RELIEF_CELLS_PER_UNIT;
      const c = sampleHypso(heightAt(x, y, params));
      const idx = (j * W + i) * 4;
      data[idx] = c.r; data[idx + 1] = c.g; data[idx + 2] = c.b; data[idx + 3] = 200;
    }
  }
  oc.putImageData(img, 0, 0);
  return off;
}

function drawRelief() {
  if (!reliefCanvas || !puzzle) return;
  const tl = toScreen({ x: 0, y: 0 });
  const br = toScreen({ x: MAP_W, y: MAP_H });
  ctx.imageSmoothingEnabled = true;
  // "high" is the slowest filter and only marginally better than "medium"
  // for our 4x-oversampled hypsometric bitmap.
  ctx.imageSmoothingQuality = "medium";
  ctx.drawImage(reliefCanvas, tl.x, tl.y, br.x - tl.x, br.y - tl.y);
}

// --- Car sprite ----------------------------------------------------------
// car.svg is a vector; a <canvas> holds pixels, so the SVG must be rasterized
// before it can be drawn. We do it ONCE, into an offscreen canvas sized to the
// car's fixed on-screen footprint (cars are constant screen size regardless of
// zoom), then blit + rotate that raster per car per frame — instead of
// re-rasterizing the vector on every drawImage call in the animation loop.
//
// It's loaded by URL (not imported through the bundler) so it behaves the same
// under the box dev server and the built Worker.
let carSprite: HTMLCanvasElement | null = null;

const CAR_PX_LEN = 18;   // on-screen car length in CSS px (matches old scale(2)*7)
const CAR_PX_WID = 9;    // on-screen car width  (matches old scale(2)*3.2)
const CAR_SS = 3;        // supersample factor for the offscreen raster (crispness)

function loadCarSprite(url = "./sprites/car.svg"): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const off = document.createElement("canvas");
      off.width = CAR_PX_LEN * CAR_SS;
      off.height = CAR_PX_WID * CAR_SS;
      const oc = off.getContext("2d")!;
      oc.imageSmoothingEnabled = true;
      oc.imageSmoothingQuality = "high";
      // Rasterize with the car pointing along +x (right), matching the angle
      // convention drawOneCar rotates by. If your car.svg points "up" instead,
      // rotate during rasterization:
      //   oc.translate(off.width / 2, off.height / 2); oc.rotate(Math.PI / 2);
      //   oc.drawImage(img, -off.height / 2, -off.width / 2, off.height, off.width);
      oc.drawImage(img, 0, 0, off.width, off.height);
      carSprite = off;
      resolve();
    };
    img.onerror = () => { console.warn(`Could not load car sprite: ${url}`); resolve(); };
    img.src = url;
  });
}

/**
 * Load every sprite the renderer needs before the first paint.
 *
 * Just the car at the moment. Kept as a named step rather than inlining
 * loadCarSprite() at the call site so adding a second asset is a one-line
 * change here instead of a change to fetchPuzzle's ordering.
 */
async function loadAllSprites(): Promise<void> {
  await Promise.all([loadCarSprite()]);
}


// Build the contour geometry. Called once per puzzle (in fetchPuzzle).
// Marching squares over the heightmap at gridRes spacing; output is flat
// segment lists per level in world coordinates.
//
// We sample at gridRes = 0.5 (twice the gameplay-cell resolution). Marching
// squares connects edge crossings with straight segments, while bilerp
// describes a hyperbolic-paraboloid surface inside each cell — so the two
// disagree most on cells with saddle (negative Gaussian) curvature.
// Halving the step makes each segment ~4× shorter, which hugs the true
// bilerp contour closely enough that probe values match the rendered
// contour to within a millimeter or two of normalized elevation.
//
// Saddle cells (4 edge crossings) need explicit disambiguation: the choice
// of which pair of crossings to connect depends on the sign of the cell's
// center value relative to the contour level. We sample the center via
// bilerp and pick the topologically-correct pairing.
function buildContourCache() {
  if (!puzzle) { cachedContours = null; return; }
  const params = puzzle.params;
  const levels: number[] = [];
  for (let l = 0.22; l <= 0.95; l += 0.04) levels.push(l);
  // MS samples one cell per heightmap cell — the natural choice now that
  // world units == heightmap cells. Each MS corner sample is therefore an
  // exact heightmap value (heightAt at integer coords passes through bilerp
  // as the cell vertex value itself), so contour endpoints are exact and
  // only the within-cell straight-vs-bilerp-hyperbola curvature is left as
  // residual error.
  const gridRes = 1;
  const cols = Math.floor(MAP_W / gridRes);
  const rows = Math.floor(MAP_H / gridRes);
  // Sample heights once; reuse across all levels.
  const grid = new Float32Array((cols + 1) * (rows + 1));
  for (let j = 0; j <= rows; j++) {
    for (let i = 0; i <= cols; i++) {
      grid[j * (cols + 1) + i] = heightAt(i * gridRes, j * gridRes, params);
    }
  }
  function H(i: number, j: number) { return grid[j * (cols + 1) + i]; }

  const out: ContourLevel[] = [];
  for (let li = 0; li < levels.length; li++) {
    const level = levels[li];
    // Reusable segment buffer per level (grow as needed).
    const segs: number[] = [];
    for (let j = 0; j < rows; j++) {
      for (let i = 0; i < cols; i++) {
        const x0 = i * gridRes, y0 = j * gridRes;
        const x1 = x0 + gridRes, y1 = y0 + gridRes;
        const v00 = H(i, j), v10 = H(i + 1, j), v01 = H(i, j + 1), v11 = H(i + 1, j + 1);
        // Inline edge-crossings: avoid closure allocation per cell. The
        // edges are labeled T(a), R(b), B(c), L(d) for the 4-crossing case.
        let ax = 0, ay = 0, bx = 0, by = 0, cx = 0, cy = 0, dx = 0, dy = 0;
        let n = 0;
        if ((v00 < level) !== (v10 < level)) {
          const t = (level - v00) / (v10 - v00);
          ax = x0 + (x1 - x0) * t; ay = y0; n++;
        }
        if ((v10 < level) !== (v11 < level)) {
          const t = (level - v10) / (v11 - v10);
          const px = x1, py = y0 + (y1 - y0) * t;
          if (n === 0) { ax = px; ay = py; } else { bx = px; by = py; }
          n++;
        }
        if ((v01 < level) !== (v11 < level)) {
          const t = (level - v01) / (v11 - v01);
          const px = x0 + (x1 - x0) * t, py = y1;
          if (n === 0) { ax = px; ay = py; }
          else if (n === 1) { bx = px; by = py; }
          else { cx = px; cy = py; }
          n++;
        }
        if ((v00 < level) !== (v01 < level)) {
          const t = (level - v00) / (v01 - v00);
          const px = x0, py = y0 + (y1 - y0) * t;
          if (n === 0) { ax = px; ay = py; }
          else if (n === 1) { bx = px; by = py; }
          else if (n === 2) { cx = px; cy = py; }
          else { dx = px; dy = py; }
          n++;
        }
        if (n === 2) {
          segs.push(ax, ay, bx, by);
        } else if (n === 4) {
          // Saddle disambiguation. The cell has crossings on all four edges
          // (T=a, R=b, B=c, L=d). Two valid pairings exist:
          //   Pairing A: (a,b)+(c,d) — T↔R and B↔L; isolates TR and BL corners
          //   Pairing B: (a,d)+(b,c) — T↔L and B↔R; isolates TL and BR corners
          // Use the cell center value (the corner average, since bilerp at
          // the center is exactly that for a bilinear surface) to pick the
          // topologically correct one. If TL and the center are on the same
          // side of `level`, TL's lobe extends through the center and joins
          // BR; the two "isolated" corners are then TR and BL, so we use
          // Pairing A. Otherwise TL is isolated in its own corner — Pairing B.
          const vCenter = (v00 + v10 + v01 + v11) * 0.25;
          const tlBelow = v00 < level;
          const centerBelow = vCenter < level;
          if (tlBelow === centerBelow) {
            // Pairing A: T↔R and B↔L.
            segs.push(ax, ay, bx, by, cx, cy, dx, dy);
          } else {
            // Pairing B: T↔L and B↔R.
            segs.push(ax, ay, dx, dy, bx, by, cx, cy);
          }
        }
      }
    }
    out.push({
      level,
      isMajor: li % 3 === 0,
      segments: new Float32Array(segs),
    });
  }
  cachedContours = out;
}

function drawContours() {
  if (!cachedContours) return;
  // Skipped during pan/zoom — see markNavigating().
  if (navigating) return;
  for (let li = 0; li < cachedContours.length; li++) {
    const cl = cachedContours[li];
    const segs = cl.segments;
    if (segs.length === 0) continue;
    const t = (cl.level - 0.22) / (0.95 - 0.22);
    const lineAlpha = cl.isMajor ? 0.55 : 0.32;
    const r = Math.round(70 + t * 130);
    const g = Math.round(55 + t * 130);
    const b = Math.round(35 + t * 130);
    ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${lineAlpha})`;
    ctx.lineWidth = cl.isMajor ? 1.1 : 0.7;
    ctx.beginPath();
    for (let i = 0; i < segs.length; i += 4) {
      const a = toScreen({ x: segs[i],     y: segs[i + 1] });
      const b2 = toScreen({ x: segs[i + 2], y: segs[i + 3] });
      ctx.moveTo(a.x, a.y); ctx.lineTo(b2.x, b2.y);
    }
    ctx.stroke();
  }
}

// Render lakes with bathymetric depth shading inside a smooth (bilerp-
// traced) shoreline outline. The outline IS the heightmap level set at
// each lake's surface elevation, so it follows the same curves as the
// elevation contour lines — no stair-step shore from cell-aligned fills.
//
// For each lake we:
//   1. Establish a clip path from the lake's outline loops (so the fill
//      can't bleed past the shore).
//   2. Fill with per-cell bathymetric rectangles inside that clip. Each
//      cell's color encodes depth = surface − heightAt(cell) — darker
//      blue for deeper water.
//   3. Stroke the outline in a deeper blue to give the shore a visible
//      edge that hides the cell-aligned fill artifacts at the boundary.
function drawLakes() {
  if (lakes.length === 0 || !puzzle) return;
  const { heightmap, width } = puzzle.params;
  const cellPx = curScale();   // screen pixels per world unit
  const inflate = 0.5;
  for (const lake of lakes) {
    if (lake.outline.length === 0) continue;
    // Build a clip path from the outline loops. Each loop is a closed
    // polyline (first === last point); fill-rule "evenodd" so islands
    // (holes) inside the outer loop don't get filled.
    ctx.save();
    ctx.beginPath();
    for (const loop of lake.outline) {
      if (loop.length < 2) continue;
      const sl = loop.map(toScreen);
      traceCatmullLoop(sl, RIVER_CURVE_TENSION);
      ctx.fillStyle = 'rgb(120, 160, 200)';
      ctx.fill();
    }
    ctx.clip("evenodd");
    // Per-cell bathymetric fill, clipped to the outline.
    for (const c of lake.cells) {
      const cellH = heightmap[c.y * width + c.x];
      const depthNorm = lake.surface - cellH;
      // Map depth [0, 0.12] (~ 0–240 m) to t in [0, 1] for the ramp.
      const t = Math.min(1, Math.max(0, depthNorm / 0.12));
      const r = Math.round(120 + (28 - 120) * t);
      const g = Math.round(160 + (60 - 160) * t);
      const b = Math.round(200 + (115 - 200) * t);
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      const tl = toScreen({ x: c.x, y: c.y });
      ctx.fillRect(tl.x - inflate, tl.y - inflate, cellPx + 2 * inflate, cellPx + 2 * inflate);
    }
    ctx.restore();
    // Outline stroke (drawn outside the clip so it sits cleanly on top).
    ctx.strokeStyle = "rgba(40, 80, 130, 0.95)";
    ctx.lineWidth = 1.2;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.beginPath();
    for (const loop of lake.outline) {
      if (loop.length < 2) continue;
      const sl = loop.map(toScreen);
      traceCatmullLoop(sl, RIVER_CURVE_TENSION);
    }
    ctx.stroke();
  }
}

// Render the drainage polylines computed server-side by computeHydrology().
// Each polyline is in world (= heightmap) coordinates ordered headwater →
// mouth, with a per-point throughflow value (upstream drainage area). We
// render each river as a series of short segments whose stroke width grows
// with the average throughflow at the segment — emulating real rivers that
// widen downstream.
// Catmull-Rom → cubic Bézier control points for the segment p1→p2, given
// the four consecutive points p0,p1,p2,p3. The curve passes through p1 and
// p2; p0 and p3 only shape the tangents. `tension` in [0,1] scales how far
// the tangents reach: 0 ≈ straight (linear), 1 ≈ full, flowing meander.
// Returns the two Bézier control points (c1 near p1, c2 near p2).
const RIVER_CURVE_TENSION = 1.0;   // flowing/meandering
function catmullRomCP(
  p0: Pt, p1: Pt, p2: Pt, p3: Pt, tension: number,
): { c1: Pt; c2: Pt } {
  // Standard Catmull-Rom-to-Bézier: control points are p1 ± (p2−p0)/6 and
  // p2 ∓ (p3−p1)/6, scaled by tension. (The /6 with tension=1 reproduces
  // the uniform Catmull-Rom spline.)
  const k = tension / 6;
  return {
    c1: { x: p1.x + (p2.x - p0.x) * k, y: p1.y + (p2.y - p0.y) * k },
    c2: { x: p2.x - (p3.x - p1.x) * k, y: p2.y - (p3.y - p1.y) * k },
  };
}

// Stroke a smooth Catmull-Rom curve through screen-space points[lo..hi]
// (inclusive) onto the current path. Caller has already issued beginPath +
// moveTo(points[lo]). `neighbors` is the full screen-space point array so
// tangents at the run's ends use the true adjacent points (avoids kinks at
// width-run boundaries); lo/hi index into it. Endpoints of the whole
// polyline duplicate their neighbor (natural clamped tangent).
function strokeCatmullRun(all: Pt[], lo: number, hi: number, tension: number) {
  for (let k = lo; k < hi; k++) {
    const p0 = all[k - 1] ?? all[k];
    const p1 = all[k];
    const p2 = all[k + 1];
    const p3 = all[k + 2] ?? all[k + 1];
    const { c1, c2 } = catmullRomCP(p0, p1, p2, p3, tension);
    ctx.bezierCurveTo(c1.x, c1.y, c2.x, c2.y, p2.x, p2.y);
  }
}

// Trace a smooth closed Catmull-Rom loop through screen-space `loop` into
// the current path. `loop` is a closed polyline whose first and last point
// are identical; we treat it as cyclic so tangents wrap around (no kink at
// the seam). Caller issues its own beginPath; this does moveTo + curves +
// closePath for one loop. Degenerate loops (<3 distinct points) fall back
// to straight segments.
function traceCatmullLoop(loop: Pt[], tension: number) {
  // Drop the duplicated closing point so indices are a clean cycle.
  const n = loop.length - 1;
  if (n < 3) {
    // Too small to smooth meaningfully — draw straight.
    ctx.moveTo(loop[0].x, loop[0].y);
    for (let i = 1; i < loop.length; i++) ctx.lineTo(loop[i].x, loop[i].y);
    return;
  }
  const at = (i: number) => loop[((i % n) + n) % n];   // cyclic index
  ctx.moveTo(at(0).x, at(0).y);
  for (let i = 0; i < n; i++) {
    const { c1, c2 } = catmullRomCP(at(i - 1), at(i), at(i + 1), at(i + 2), tension);
    const p2 = at(i + 1);
    ctx.bezierCurveTo(c1.x, c1.y, c2.x, c2.y, p2.x, p2.y);
  }
  ctx.closePath();
}

function drawRivers() {
  if (rivers.length === 0) return;
  // Width is roughly proportional to the log of throughflow. The baseline
  // keeps small tributaries visible; the multiplier sets how dramatically
  // big rivers widen relative to small ones. 1.2 gives a noticeably
  // stronger spread than the original 0.6.
  function widthFor(throughflow: number) {
    return 0.6 + 1.2 * Math.log10(Math.max(1, throughflow));
  }
  ctx.strokeStyle = "rgba(48, 92, 138, 0.95)";
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (const river of rivers) {
    const pts = river.points;
    const tf = river.throughflow;
    if (pts.length < 2) continue;
    // Precompute screen-space points once. Catmull-Rom tangents reference
    // neighbors across width-run boundaries, so we need the whole array up
    // front rather than per-run.
    const sp: Pt[] = pts.map(toScreen);
    // Group consecutive segments with the same width into single sub-paths
    // to reduce stroke() calls. The width-from-log function is smooth, so
    // we quantize widths to nearest 0.25 px before grouping. Each run is
    // stroked as a smooth curve; tangents at run ends still use the true
    // adjacent points (via the full `sp` array) so curves don't kink where
    // the width changes.
    let i = 0;
    while (i < pts.length - 1) {
      const segWidth = (widthFor(tf[i]) + widthFor(tf[i + 1])) * 0.5;
      const wQ = Math.round(segWidth * 4) / 4;  // quantize to 0.25
      // Extend run while neighboring segments share the same quantized width.
      let j = i + 1;
      while (j < pts.length - 1) {
        const w2 = (widthFor(tf[j]) + widthFor(tf[j + 1])) * 0.5;
        if (Math.round(w2 * 4) / 4 !== wQ) break;
        j++;
      }
      ctx.lineWidth = wQ;
      ctx.beginPath();
      ctx.moveTo(sp[i].x, sp[i].y);
      strokeCatmullRun(sp, i, j, RIVER_CURVE_TENSION);
      ctx.stroke();
      i = j;
    }
  }
}

function drawGrid() {
  // Per-cell debug grid: thin lines at half-integer map coordinates
  // (x = 0.5, 1.5, ... and similarly for y) — between cell centers, so
  // each heightmap cell is bounded by a unit square of grid lines.
  //
  // Drawn per-frame in screen space (rather than baked into a hi-res
  // offscreen canvas) because that's the only way to get pixel-crisp
  // lines at every zoom: any pre-rendered canvas has to be scaled at
  // draw time and antialiasing softens the lines. Per-frame screen-
  // space draw is cheap (O(MAP_W + MAP_H) line segments, each a few
  // arithmetic ops) so the cost is negligible.
  ctx.strokeStyle = "rgba(20, 20, 20, 0.35)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let mx = 0.5; mx < MAP_W; mx += 1) {
    const a = toScreen({ x: mx, y: 0 }), b = toScreen({ x: mx, y: MAP_H });
    // +0.5 puts the 1-px stroke on a pixel center rather than straddling
    // two — the standard canvas crisp-line trick.
    const sx = Math.round(a.x) + 0.5;
    ctx.moveTo(sx, a.y); ctx.lineTo(sx, b.y);
  }
  for (let my = 0.5; my < MAP_H; my += 1) {
    const a = toScreen({ x: 0, y: my }), b = toScreen({ x: MAP_W, y: my });
    const sy = Math.round(a.y) + 0.5;
    ctx.moveTo(a.x, sy); ctx.lineTo(b.x, sy);
  }
  ctx.stroke();
}

function drawExistingRoad() {
  if (!puzzle) return;
  ctx.strokeStyle = "rgba(160, 64, 48, 0.75)";
  ctx.lineWidth = 2.5;
  ctx.setLineDash([6, 4]);
  ctx.beginPath();
  for (let i = 0; i < puzzle.existingRoad.length; i++) {
    const p = toScreen(puzzle.existingRoad[i]);
    if (i === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  }
  ctx.stroke();
  ctx.setLineDash([]);
}

// =============================================================
// DEBUG GRADE VIZ
// =============================================================
function gradeColor(grade: number): string {
  const g = Math.abs(grade);
  const stops: Array<[number, number, number, number]> = [
    [0.00, 40, 160, 60],
    [0.035, 220, 200, 50],
    [0.07, 230, 130, 20],
    [0.10, 220, 40, 40],
    [0.15, 130, 10, 10],
  ];
  for (let i = 1; i < stops.length; i++) {
    if (g <= stops[i][0]) {
      const a = stops[i - 1], b = stops[i];
      const t = (g - a[0]) / (b[0] - a[0]);
      return `rgb(${Math.round(a[1] + (b[1] - a[1]) * t)}, ${Math.round(a[2] + (b[2] - a[2]) * t)}, ${Math.round(a[3] + (b[3] - a[3]) * t)})`;
    }
  }
  const last = stops[stops.length - 1];
  return `rgb(${last[1]}, ${last[2]}, ${last[3]})`;
}

function drawGradeColoredRoad() {
  if (!lastAnalysis) return;
  const { samples, z } = lastAnalysis;
  if (!samples || samples.length < 2) return;
  ctx.lineCap = "round"; ctx.lineJoin = "round";
  ctx.strokeStyle = "rgba(241, 232, 211, 0.7)"; ctx.lineWidth = 7;
  ctx.beginPath();
  for (let i = 0; i < samples.length; i++) {
    const p = toScreen(samples[i]);
    if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
  }
  ctx.stroke();
  ctx.lineWidth = 4; ctx.lineCap = "butt";
  for (let i = 1; i < samples.length; i++) {
    const ds = samples[i].s - samples[i - 1].s;
    if (ds < 1e-6) continue;
    const grade = Math.abs((z[i] - z[i - 1]) / ds);
    ctx.strokeStyle = gradeColor(grade);
    const a = toScreen(samples[i - 1]);
    const b = toScreen(samples[i]);
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
  }
}

// =============================================================
// PROPOSED ROAD (multi-color via De Casteljau)
// =============================================================

function drawProposedRoad() {
  if (!lastAnalysis) return;
  if (DEBUG_GRADE_VIZ) { drawGradeColoredRoad(); return; }
  const { samples, z, kinds } = lastAnalysis;
  if (!samples || samples.length < 2) return;
  const arcInfo = buildArcTables();
  ctx.lineCap = "round"; ctx.lineJoin = "round";
  // halo
  ctx.strokeStyle = "rgba(241, 232, 211, 0.7)"; ctx.lineWidth = 6.5;
  strokeSmoothBezier();
  // runs
  const runs = findStructureRuns(kinds);
  const runPaths = runs.map(r => ({
    kind: r.kind,
    cubics: getCubicSubpath(samples[r.startIdx].s, samples[r.endIdx].s, arcInfo),
    run: r,
  }));

  // --- Depth/height helpers ----------------------------------------------
  // Road height above (positive) or below (negative) terrain, in *meters*,
  // averaged across segment (i, i+1) — used to size that segment's render.
  // zT and z are both in km.
  function segDeltaM(i: number): number {
    return ((z[i] + z[i + 1]) * 0.5 - (samples[i].zT + samples[i + 1].zT) * 0.5) * 1000;
  }

  // Tunable mapping from depth/height (meters) to visual size (pixels).
  // Tunnels: line width grows with depth from ~13m (the threshold) up to
  // hundreds of meters. Cap at TUNNEL_MAX_PX so deep tunnels stay readable
  // without dominating the map.
  const TUNNEL_BASE_PX = 2.5;
  const TUNNEL_PER_M = 0.04;     // 50m → 4.5px, 200m → 10.5px
  const TUNNEL_MAX_PX = 14;
  function tunnelWidth(depthM: number) {
    return Math.min(TUNNEL_MAX_PX, TUNNEL_BASE_PX + depthM * TUNNEL_PER_M);
  }
  // Opencuts: shorter depth range (1-13m typical), so a smaller scale.
  const OPENCUT_BASE_PX = 2;
  const OPENCUT_PER_M = 0.18;    // 5m → 2.9px, 12.5m → 4.25px
  const OPENCUT_MAX_PX = 5.5;
  function opencutWidth(depthM: number) {
    return Math.min(OPENCUT_MAX_PX, OPENCUT_BASE_PX + depthM * OPENCUT_PER_M);
  }
  // Bridge deck ticks: grow with deck height above terrain.
  const TICK_BASE_PX = 3;
  const TICK_PER_M = 0.18;       // 5m → 3.9px, 50m → 12px
  const TICK_MAX_PX = 14;
  function tickHalfPx(heightM: number) {
    return Math.min(TICK_MAX_PX, TICK_BASE_PX + Math.abs(heightM) * TICK_PER_M);
  }

  // Helper: stroke samples[startIdx..endIdx] as straight per-sample dashes,
  // each segment's line width derived from its segDeltaM. Used for tunnels
  // and opencuts; the dashed pattern hides the small polygonal kink at
  // sample joins.
  function strokeStructureRunPerSample(
    startIdx: number, endIdx: number,
    width: (depthM: number) => number,
    color: string, dash: number[]
  ) {
    ctx.strokeStyle = color;
    ctx.setLineDash(dash);
    for (let i = startIdx; i < endIdx; i++) {
      const a = toScreen(samples[i]);
      const b = toScreen(samples[i + 1]);
      // Depth is negative for cuts/tunnels — use magnitude.
      const depthM = Math.abs(segDeltaM(i));
      ctx.lineWidth = width(depthM);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
  }

  // Paint order: deepest/most-hidden first, so subsequent layers cleanly
  // overdraw at junctions. Tunnels are fully underground (drawn first),
  // open cuts are below ground but visible (drawn next), at-grade roads
  // overdraw opencut dashes at junctions for continuity, bridges sit on
  // top of everything.
  // "fill" draws with the at-grade pass: it's a surface road, just one on an
  // embankment, and it must not overdraw the bridge layer.
  const order: Kind[] = ["tunnel", "opencut", "grade", "fill", "bridge"];
  for (const target of order) {
    for (const rp of runPaths) {
      if (rp.kind !== target) continue;
      if (rp.kind === "tunnel") {
        strokeStructureRunPerSample(
          rp.run.startIdx, rp.run.endIdx,
          tunnelWidth,
          "rgba(60, 60, 60, 0.95)", [7, 5],
        );
      } else if (rp.kind === "opencut") {
        // Green dashed: "below ground but not buried". Shorter dash than
        // tunnels so the two are visually distinct at a glance.
        strokeStructureRunPerSample(
          rp.run.startIdx, rp.run.endIdx,
          opencutWidth,
          "rgba(60, 130, 60, 0.95)", [4, 3],
        );
      } else if (rp.kind === "bridge") {
        // Bridges keep the smooth cubic — the deck ticks (below) carry the
        // height encoding so the centerline can stay continuous.
        ctx.strokeStyle = "rgba(200, 168, 56, 1.0)"; ctx.lineWidth = 4;
        ctx.setLineDash([]);
        strokeCubicSubpath(rp.cubics);
      } else if (rp.kind === "fill") {
        // Embankment: solid like at-grade (it IS a surface road), but in the
        // earth tone and a touch heavier, so a raised alignment is legible
        // without implying a structure.
        ctx.strokeStyle = "rgba(120, 96, 40, 0.95)"; ctx.lineWidth = 3.5;
        ctx.setLineDash([]);
        strokeCubicSubpath(rp.cubics);
      } else {
        ctx.strokeStyle = "rgba(26, 74, 26, 0.95)"; ctx.lineWidth = 3;
        ctx.setLineDash([]);
        strokeCubicSubpath(rp.cubics);
      }
    }
  }
  ctx.setLineDash([]);
  // Bridge deck ticks, with per-segment length proportional to deck height
  // above terrain. Iterating per-sample (i += 1) instead of every other
  // sample so the tick density matches the road's sample resolution and
  // height variation is legible.
  ctx.strokeStyle = "rgba(120, 90, 30, 0.9)"; ctx.lineWidth = 1.2;
  for (const rp of runPaths) {
    if (rp.kind !== "bridge") continue;
    for (let i = rp.run.startIdx; i < rp.run.endIdx; i++) {
      const a = samples[i], b = samples[i + 1];
      const sa = toScreen(a), sb = toScreen(b);
      const dx = sb.x - sa.x, dy = sb.y - sa.y;
      const len = Math.hypot(dx, dy);
      if (len < 4) continue;
      const nx = -dy / len, ny = dx / len;
      const mx = (sa.x + sb.x) / 2, my = (sa.y + sb.y) / 2;
      const half = tickHalfPx(segDeltaM(i));
      ctx.beginPath();
      ctx.moveTo(mx + nx * half, my + ny * half);
      ctx.lineTo(mx - nx * half, my - ny * half);
      ctx.stroke();
    }
  }
}

// =============================================================
// HANDLES & TOWNS
// =============================================================

function drawHandles() {
  // leash lines
  for (let i = 0; i < road.anchors.length; i++) {
    const a = road.anchors[i], h = road.handles[i];
    const sa = toScreen(a);
    ctx.strokeStyle = "rgba(42, 122, 42, 0.5)"; ctx.lineWidth = 2;
    ctx.setLineDash([3, 3]);
    if (h.in)  { const sh = toScreen(h.in);  ctx.beginPath(); ctx.moveTo(sa.x, sa.y); ctx.lineTo(sh.x, sh.y); ctx.stroke(); }
    if (h.out) { const sh = toScreen(h.out); ctx.beginPath(); ctx.moveTo(sa.x, sa.y); ctx.lineTo(sh.x, sh.y); ctx.stroke(); }
    ctx.setLineDash([]);
  }
  // handle dots
  for (let i = 0; i < road.anchors.length; i++) {
    const h = road.handles[i];
    if (h.in)  drawHandleDot(toScreen(h.in));
    if (h.out) drawHandleDot(toScreen(h.out));
  }
  // anchors (skip endpoints)
  for (let i = 1; i < road.anchors.length - 1; i++) drawAnchorDot(toScreen(road.anchors[i]));
}
function drawHandleDot(p: Pt) {
  ctx.fillStyle = "rgba(241, 232, 211, 0.5)"; ctx.strokeStyle = "#2a7a2a"; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.arc(p.x, p.y, 7, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
}
function drawAnchorDot(p: Pt) {
  ctx.fillStyle = "#f1e8d3"; ctx.strokeStyle = "#1a4a1a"; ctx.lineWidth = 2.5;
  ctx.beginPath(); ctx.arc(p.x, p.y, 8, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
}

// =============================================================
// DEBUG OVERLAYS
// =============================================================

// Skeleton overlay: trunk polyline + MST edges + peaks. Drawn on the
// dynamic layer (so toggling doesn't invalidate the static composite).
// Edge thickness encodes hierarchical weight; trunk is rendered slightly
// thicker than the heaviest branch for legibility.
function drawSkeleton() {
  if (!skeleton) return;
  // Trunk centerline.
  ctx.strokeStyle = "rgba(220, 40, 200, 0.85)";
  ctx.lineWidth = 3;
  ctx.setLineDash([]);
  ctx.beginPath();
  for (let i = 0; i < skeleton.trunk.length; i++) {
    const p = toScreen(skeleton.trunk[i]);
    if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
  }
  ctx.stroke();
  // MST edges. Thicker for higher-weight (trunk-adjacent) branches.
  for (const e of skeleton.edges) {
    const a = toScreen({ x: e.ax, y: e.ay });
    const b = toScreen({ x: e.bx, y: e.by });
    const alpha = 0.45 + 0.45 * e.weight;
    const width = 1 + 2 * e.weight;
    ctx.strokeStyle = `rgba(255, 80, 220, ${alpha.toFixed(2)})`;
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
    ctx.stroke();
  }
  // Peaks as dots, sized by the weight of their incoming edge. We don't
  // have direct access to "this peak's weight" here, so estimate from the
  // edges that connect to this peak.
  const peakWeights = new Float32Array(skeleton.peaks.length);
  for (const e of skeleton.edges) {
    for (let pi = 0; pi < skeleton.peaks.length; pi++) {
      const p = skeleton.peaks[pi];
      // A peak is an endpoint of its parent edge; either (ax,ay) or (bx,by).
      if ((Math.abs(p.x - e.ax) < 1e-6 && Math.abs(p.y - e.ay) < 1e-6) ||
          (Math.abs(p.x - e.bx) < 1e-6 && Math.abs(p.y - e.by) < 1e-6)) {
        if (e.weight > peakWeights[pi]) peakWeights[pi] = e.weight;
      }
    }
  }
  ctx.fillStyle = "rgba(255, 240, 60, 1.0)";
  ctx.strokeStyle = "rgba(120, 30, 0, 0.9)";
  ctx.lineWidth = 1.5;
  for (let i = 0; i < skeleton.peaks.length; i++) {
    const p = toScreen(skeleton.peaks[i]);
    const w = peakWeights[i];
    const r = 4 + 4 * w;
    ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    ctx.fill(); ctx.stroke();
  }
  // Tiny weight labels on peaks (only at decent zoom — clutter otherwise).
  if (curScale() > 1.2) {
    ctx.fillStyle = "rgba(20, 20, 20, 0.95)";
    ctx.font = "10px monospace";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    for (let i = 0; i < skeleton.peaks.length; i++) {
      const p = toScreen(skeleton.peaks[i]);
      ctx.fillText(peakWeights[i].toFixed(2), p.x + 10, p.y);
    }
  }
}

// Elevation probe overlay: render each probe's heightAt() value as text
// at the probe's screen position. Background pill for legibility against
// the relief.
function drawElevationProbes() {
  if (!puzzle || elevationProbes.length === 0) return;
  ctx.font = "12px monospace";
  ctx.textBaseline = "middle";
  for (const probe of elevationProbes) {
    const h = heightAt(probe.x, probe.y, puzzle.params);
    const zKm = h * HEIGHT_SCALE_KM;
    const sp = toScreen(probe);
    // Format: "0.42  →  840 m"
    // const text = `${h.toFixed(3)}  ${Math.round(zKm * 1000)} m`;
    const text = `${Math.round(zKm * 1000)}`;
    const metrics = ctx.measureText(text);
    const pad = 4, w = metrics.width + pad * 2, halfH = 8;
    // Pill background.
    ctx.fillStyle = "rgba(241, 232, 211, 0.92)";
    ctx.strokeStyle = "rgba(20, 20, 20, 0.85)";
    ctx.lineWidth = 1;
    const x0 = sp.x + 8, y0 = sp.y - halfH;
    ctx.beginPath();
    ctx.rect(x0, y0, w, halfH * 2);
    ctx.fill(); ctx.stroke();
    // Crosshair dot at the actual probe location.
    ctx.fillStyle = "rgba(220, 40, 80, 1.0)";
    ctx.beginPath(); ctx.arc(sp.x, sp.y, 3, 0, Math.PI * 2); ctx.fill();
    // Text.
    ctx.fillStyle = "rgba(20, 20, 20, 1.0)";
    ctx.textAlign = "left";
    ctx.fillText(text, x0 + pad, sp.y);
  }
}

// Fill a list of cells with a solid color. Cells occupy a 1×1 map-unit
// square centered at their integer coordinate, so corners are at
// (c.x±0.5, c.y±0.5) — exactly where the debug grid lines run, so the
// fill sits flush within one grid square.
function shadeCells(
  cells: Array<{ x: number; y: number }>,
  fillStyle: string,
) {
  if (cells.length === 0) return;
  ctx.fillStyle = fillStyle;
  for (const c of cells) {
    const tl = toScreen({ x: c.x - 0.5, y: c.y - 0.5 });
    const br = toScreen({ x: c.x + 0.5, y: c.y + 0.5 });
    ctx.fillRect(tl.x, tl.y, br.x - tl.x, br.y - tl.y);
  }
}

// Spillway shading: red for each lake's spillway cell (where water exits),
// orange for the discoverer cell (the lake-perimeter cell that first
// reached the spillway during the flood fill). Discoverers drawn after
// spillways; in the normal case they're adjacent, not overlapping, but if
// they ever coincide the orange wins (drawn last). Both populated only
// when the page was loaded with ?debug=true.
function drawSpillways() {
  shadeCells(spillwayCells, "rgba(220, 40, 40, 0.45)");
  shadeCells(discovererCells, "rgba(240, 140, 20, 0.45)");
}

// Drainage label overlay: render each above-threshold cell's drainage value
// as a tiny number at the cell's screen position. Populated only when the
// page was loaded with ?debug=true (server omits the data otherwise). At
// low zoom the labels overlap heavily, so we skip drawing them until the
// view is zoomed in enough to make them legible.
function drawDrainageLabels() {
  if (drainageCells.length === 0) return;
  const scale = curScale();
  // Below ~0.8× the labels are illegible spaghetti. Skip rather than draw
  // a useless smear.
  if (scale < 0.8) return;
  // Decimate at low-to-mid zoom: only draw labels for the highest-drainage
  // cells when many would overlap. At high zoom (>2×) show everything.
  const minD = scale >= 2 ? 0 : (scale >= 1.2 ? 10 : 25);
  ctx.font = "9px monospace";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "rgba(0, 80, 180, 0.95)";
  ctx.strokeStyle = "rgba(255, 255, 255, 0.85)";
  ctx.lineWidth = 2.5;
  for (const c of drainageCells) {
    if (c.d < minD) continue;
    // Cells are centered at integer map coordinates (the heightmap sample
    // position), with half-integer coordinates lying on cell boundaries
    // — which is where the debug gridlines run. So the label for cell
    // (c.x, c.y) goes at (c.x, c.y), not (c.x + 0.5, c.y + 0.5).
    const sp = toScreen({ x: c.x, y: c.y });
    // Round to nearest int — fractional drainage isn't useful for debug.
    const text = String(Math.round(c.d));
    // White stroke as a halo so the number stays legible over any relief.
    ctx.strokeText(text, sp.x, sp.y);
    ctx.fillText(text, sp.x, sp.y);
  }
}

// =============================================================
// TOWN LAYOUT
/**
 * Deterministic hash-based RNG in [0, 1). `key` identifies the thing being
 * randomised (a town seed, a block seed) and `i` selects an independent
 * stream for that thing, so a given (key, i) always yields the same value.
 *
 * This is what keeps town layouts stable across redraws and identical for
 * every player on a given day, without threading a stateful generator
 * through the draw path. It lived with the tree scatter until that was
 * removed; the town renderer is now the only caller.
 */
function seededRand(key: number, i: number): number {
  let h = (key * 374761393 + i * 668265263) | 0;
  h = (h ^ (h >>> 13)) * 1274126177;
  h = h ^ (h >>> 16);
  return ((h >>> 0) / 4294967295);
}

// =============================================================
//
// Each town is rendered as a grid of city blocks: a dense 3x3 core plus
// 5-9 irregular outskirt blocks chosen to be adjacent to the existing
// footprint. The grid is rotated to a town-specific angle for visual
// variety. Each block has a dark "street" base with a small building
// shape on top.

type TownBlock = { ix: number; iy: number; bSeed: number };
type TownLayout = {
  blocks: TownBlock[];
  rotation: number;        // radians
  cx: number; cy: number;  // world center
};

const TOWN_BLOCK_SIZE = 1.1;    // world units per block edge
const TOWN_CORE = 3;            // core is TOWN_CORE x TOWN_CORE blocks
const TOWN_EXTRAS_MIN = 5;
const TOWN_EXTRAS_MAX = 9;

let townLayouts: TownLayout[] = [];

function buildTownLayouts(p: Puzzle) {
  townLayouts = p.towns.map((t, idx) => {
    const seed = ((Math.floor(t.x * 1000) ^ Math.floor(t.y * 1000)) | 0) ^ (p.seed + idx * 1009);
    return buildOneTown(t.x, t.y, seed);
  });
}

function buildOneTown(cx: number, cy: number, seed: number): TownLayout {
  // 3x3 core: ix, iy in [-1, 0, 1]
  const half = Math.floor(TOWN_CORE / 2);
  const set = new Set<string>();
  const blocks: TownBlock[] = [];
  function add(ix: number, iy: number) {
    const k = `${ix},${iy}`;
    if (set.has(k)) return false;
    set.add(k);
    blocks.push({ ix, iy, bSeed: ((seed * 37 + ix * 911 + iy * 6151) | 0) });
    return true;
  }
  for (let dy = -half; dy <= half; dy++) {
    for (let dx = -half; dx <= half; dx++) add(dx, dy);
  }
  // Outskirts: 5-9 extras, each adjacent to an existing block.
  const nExtras = TOWN_EXTRAS_MIN + Math.floor(seededRand(seed, 1) * (TOWN_EXTRAS_MAX - TOWN_EXTRAS_MIN + 1));
  for (let i = 0; i < nExtras; i++) {
    // Pick a frontier candidate: a non-occupied cell adjacent to an occupied one.
    // Enumerate frontier, then choose one by seeded random.
    const frontier: Array<{ ix: number; iy: number }> = [];
    for (const b of blocks) {
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nx = b.ix + dx, ny = b.iy + dy;
        if (!set.has(`${nx},${ny}`)) frontier.push({ ix: nx, iy: ny });
      }
    }
    if (frontier.length === 0) break;
    const pick = frontier[Math.floor(seededRand(seed, 100 + i) * frontier.length)];
    add(pick.ix, pick.iy);
  }
  // Rotation: small per-town variation
  const rotation = (seededRand(seed, 7) - 0.5) * (Math.PI / 4);  // ±22.5°
  return { blocks, rotation, cx, cy };
}

function drawTownLayout(layout: TownLayout) {
  const { blocks, rotation, cx, cy } = layout;
  const center = toScreen({ x: cx, y: cy });
  const blockPx = TOWN_BLOCK_SIZE * curScale();   // block edge in screen px
  ctx.save();
  ctx.translate(center.x, center.y);
  ctx.rotate(rotation);

  // First pass: street/base for all blocks. Slightly larger than the
  // building rectangle so visible gaps form streets between buildings.
  ctx.fillStyle = "#3a342a";
  for (const b of blocks) {
    const x = b.ix * blockPx - blockPx / 2;
    const y = b.iy * blockPx - blockPx / 2;
    ctx.fillRect(x, y, blockPx, blockPx);
  }

  // Second pass: per-block building, inset from the street base.
  for (const b of blocks) {
    const x = b.ix * blockPx;
    const y = b.iy * blockPx;
    drawBuilding(x, y, blockPx, b.bSeed);
  }
  ctx.restore();
}

/** [r, g, b] in 0..255 -> "rrggbb" (no leading #). */
function toHexString(color: readonly number[]): string {
  const [r, g, b] = color;
  return r.toString(16).padStart(2, "0")
    + g.toString(16).padStart(2, "0")
    + b.toString(16).padStart(2, "0");
}

/**
 * Draw one isometric building, centred on a block.
 *
 * Everything after ctx.scale(s, s) is in LOCAL design units, not screen px:
 * lineWidth and coordinates alike get multiplied by s, which is what keeps the
 * building proportional across zoom levels.
 */
function drawBuilding(cxScreen: number, cyScreen: number, blockPx: number, seed: number): void {
  const roofColors = [[72, 95, 95], [54, 64, 74], [84, 99, 115]];
  const wallColors = [[245, 245, 220], [160, 82, 45], [255, 253, 208]];

  const roof = roofColors[Math.floor(seededRand(seed, 1) * roofColors.length)];
  const wall = wallColors[Math.floor(seededRand(seed, 2) * wallColors.length)];

  const DESIGN_UNITS = 12;            // how many local units span the block (tune to taste)
  const s = blockPx / DESIGN_UNITS;   // screen px per local unit — PROPORTIONAL to zoom now

  ctx.save();
  ctx.translate(cxScreen, cyScreen);  // anchor at block center
  ctx.scale(s, s);                    // everything below is in LOCAL units
  ctx.translate(0, 6);               // recenter the design over the anchor (tune)
  const transform = ctx.getTransform();
  if ((transform.b + transform.d) > 0) {
      ctx.rotate(Math.PI);
  }

  ctx.lineWidth = 0.08;               // set in LOCAL units, since scale multiplies it

  // Columns are the projected x, y and z axes. Named toLocal rather than
  // toScreen because the module-level toScreen(p) is a different projection
  // entirely and shadowing it here reads as a bug.
  const basisVectors = [[0.8, 1], [-1.6, 1], [0, 1]];
  function toLocal(x: number, y: number, z: number): [number, number] {
    return [
      2 + x * basisVectors[0][0] + y * basisVectors[1][0] + z * basisVectors[2][0],
      2 + x * basisVectors[0][1] + y * basisVectors[1][1] + z * basisVectors[2][1],
    ];
  }
  /** Project (x, y, z) and hand the 2D result to a ctx path method. */
  const coordCall = (
    x: number, y: number, z: number,
    fn: (px: number, py: number) => void,
  ): void => { const [px, py] = toLocal(x, y, z); fn(px, py); };

  ctx.strokeStyle = "#333";

  // Yard.
  const r = Math.floor(80 + 48 * seededRand(seed, 3));
  const g = Math.floor(96 + 16 * seededRand(seed, 4));
  const b = Math.floor(48 * seededRand(seed, 5));
  const lawnColor = "#" + toHexString([r, g, b]);

  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.rect(-4.5, 1.5, 9, 9);
  ctx.fillStyle = lawnColor;
  ctx.fill();

  // Only one building style so far; seededRand(seed, 0) is reserved for the
  // style roll so adding a second one doesn't reshuffle every other draw.
  {
    // First wall:
    ctx.beginPath();
    ctx.moveTo(2, 2);
    coordCall(3, 0, 0, ctx.lineTo.bind(ctx));
    coordCall(3, 0, 2, ctx.lineTo.bind(ctx));
    coordCall(1.5, 0, 2.5, ctx.lineTo.bind(ctx));
    coordCall(0, 0, 2, ctx.lineTo.bind(ctx));
    ctx.closePath();
    ctx.fillStyle = "#" + toHexString(wall);
    ctx.fill();
    ctx.stroke();

    // Second wall:
    ctx.beginPath();
    ctx.moveTo(2, 2);
    coordCall(0, 3, 0, ctx.lineTo.bind(ctx));
    coordCall(0, 3, 2, ctx.lineTo.bind(ctx));
    coordCall(0, 0, 2, ctx.lineTo.bind(ctx));
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // First roof:
    ctx.beginPath();
    coordCall(0, 0, 2, ctx.moveTo.bind(ctx));
    coordCall(0, 3, 2, ctx.lineTo.bind(ctx));
    coordCall(0, 3, 2.5, ctx.lineTo.bind(ctx));
    coordCall(1.5, 3, 2.5, ctx.lineTo.bind(ctx));
    coordCall(1.5, 0, 2.5, ctx.lineTo.bind(ctx));
    ctx.closePath();
    ctx.fillStyle = "#" + toHexString(roof);
    ctx.fill();
    ctx.stroke();

    // Second roof face — darkened, since it turns away from the light.
    // (This assignment used to be overwritten by a second `fillStyle = roof`
    // immediately before the fill, so both faces came out the same colour and
    // the roof read as flat.)
    ctx.fillStyle = "#" + toHexString(roof.map((c) => Math.floor(0.7 * c)));
    ctx.beginPath();
    coordCall(3, 0, 2, ctx.moveTo.bind(ctx));
    coordCall(3, 3, 2, ctx.lineTo.bind(ctx));
    coordCall(3, 3, 2.5, ctx.lineTo.bind(ctx));
    coordCall(1.5, 3, 2.5, ctx.lineTo.bind(ctx));
    coordCall(1.5, 0, 2.5, ctx.lineTo.bind(ctx));
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }

  ctx.restore();
}

function drawTowns() {
  if (!puzzle || townLayouts.length === 0) return;
  for (const layout of townLayouts) drawTownLayout(layout);
}

function drawTownLabels() {
  if (!puzzle) return;
  ctx.fillStyle = "#2a3a2e";
  ctx.strokeStyle = "rgba(241, 232, 211, 0.85)";
  ctx.lineWidth = 3;
  ctx.font = "bold 13px Georgia, serif";
  ctx.textBaseline = "middle";
  for (const t of puzzle.towns) {
    const p = toScreen(t);
    // Estimate town footprint to push the label away from it
    const offset = TOWN_BLOCK_SIZE * 3 * curScale();
    const labelX = t.x > MAP_W / 2 ? p.x - offset : p.x + offset;
    ctx.textAlign = t.x > MAP_W / 2 ? "right" : "left";
    const label = t.name.toUpperCase();
    // Cream halo for legibility against the town graphic
    ctx.strokeText(label, labelX, p.y);
    ctx.fillText(label, labelX, p.y);
  }
}

// =============================================================
// =============================================================
// CAR ANIMATION
// =============================================================
//
// Cars are placed along the road at fixed *travel-time* intervals
// (one car per CAR_INTERVAL_SEC of trip time). The animation loops over
// a fixed wall-clock period, so the road's full trip is replayed every
// ANIMATION_PERIOD_SEC. This keeps the animation watchable regardless
// of how long the simulated trip would take.
//
// To find a car's position at fractional trip-time t:
//   1. Use the cumulative-time-per-sample array (built from velocity profile)
//   2. Binary-search for the sample bracketing t
//   3. Linearly interpolate position and orientation

let SHOW_CARS = true;
let lastTimeTable: number[] | null = null;  // cumulative trip time in seconds per sample
let lastTripTime = 0;                       // total trip time in seconds
const CAR_INTERVAL_SEC = 360;               // one car per 6 in-game-minutes of trip time
const ANIMATION_PERIOD_SEC = 30;            // full trip replays every 30s
const MAX_CARS = 60;                        // safety cap

function rebuildTimeTable() {
  // Build cumulative-time-per-sample from the velocity profile.
  // Called whenever the analysis changes (i.e., from updatePanel).
  if (!lastAnalysis) { lastTimeTable = null; lastTripTime = 0; return; }
  const { samples, speeds } = lastAnalysis;
  const N = samples.length;
  if (N < 2) { lastTimeTable = null; lastTripTime = 0; return; }
  const table = new Array(N);
  table[0] = 0;
  for (let i = 1; i < N; i++) {
    const dsKm = samples[i].s - samples[i - 1].s;
    const v1 = Math.max(5, speeds[i - 1]);
    const v2 = Math.max(5, speeds[i]);
    const dtHours = 0.5 * (dsKm / v1 + dsKm / v2);
    table[i] = table[i - 1] + dtHours * 3600;
  }
  lastTimeTable = table;
  lastTripTime = table[N - 1];
}

// Given a trip time (seconds), return { x, y, angle } interpolating linearly
// between samples. Returns null if the time is out of range.
function carAt(tripSec: number): { x: number; y: number; angle: number } | null {
  if (!lastAnalysis || !lastTimeTable) return null;
  const { samples } = lastAnalysis;
  if (tripSec < 0 || tripSec > lastTripTime) return null;
  // Binary search
  let lo = 0, hi = lastTimeTable.length - 1;
  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (lastTimeTable[mid] <= tripSec) lo = mid;
    else hi = mid;
  }
  const t0 = lastTimeTable[lo], t1 = lastTimeTable[hi];
  const f = (t1 - t0) > 1e-9 ? (tripSec - t0) / (t1 - t0) : 0;
  const a = samples[lo], b = samples[hi];
  const x = a.x + (b.x - a.x) * f;
  const y = a.y + (b.y - a.y) * f;
  const angle = Math.atan2(b.y - a.y, b.x - a.x);
  return { x, y, angle };
}

function drawCars() {
  if (!SHOW_CARS || !lastAnalysis || !lastTimeTable || lastTripTime <= 0) return;

  // Wall-clock advances the simulated trip time at a rate of
  // (lastTripTime / ANIMATION_PERIOD_SEC) trip-seconds per real-second.
  // Each car has a fixed offset in trip-time, modulated independently so
  // that wrapping looks smooth (cars cycle off the end and reappear at
  // the start at different times, rather than all jumping at once).
  const tripRate = 4 * (lastTripTime / ANIMATION_PERIOD_SEC) / Math.pow(curScale(), 0.75);
  const wallSec = performance.now() / 1000;
  const phase = wallSec * tripRate;

  const nCars = Math.min(MAX_CARS, Math.floor(lastTripTime / CAR_INTERVAL_SEC) + 1);
  for (let i = 0; i < nCars; i++) {
    let t = (phase - i * CAR_INTERVAL_SEC) % lastTripTime;
    if (t < 0) t += lastTripTime;
    const pos = carAt(t);
    if (!pos) continue;
    drawOneCar(pos.x, pos.y, pos.angle);
  }
}

function drawOneCar(wx: number, wy: number, angle: number) {
  const p = toScreen({ x: wx, y: wy });
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(angle);
  if (carSprite) {
    // Source is a supersampled raster; blit at the CSS-pixel car size.
    ctx.drawImage(carSprite, -CAR_PX_LEN / 2, -CAR_PX_WID / 2, CAR_PX_LEN, CAR_PX_WID);
  } else {
    // Fallback: procedural body until the sprite finishes loading (or if the
    // file is missing).
    ctx.scale(2, 2);
    // Body — small rectangle, ~7px long, ~3px tall (in screen pixels)
    ctx.fillStyle = "#1a4a1a";
    ctx.fillRect(-3.5, -1.6, 7, 3.2);
    // Hint of windshield/cab — pale rectangle at the front
    ctx.fillStyle = "#f1e8d3";
    ctx.fillRect(0.5, -1.2, 1.6, 2.4);
    // Subtle outline
    ctx.strokeStyle = "#0a2a0a";
    ctx.lineWidth = 0.6;
    ctx.strokeRect(-3.5, -1.6, 7, 3.2);
  }
  ctx.restore();
}

// =============================================================
// MAIN DRAW & RESIZE
// =============================================================

function draw() {
  updatePanel();
  rebuildTimeTable();
  redraw();
}

// Renders are coalesced to at most one per animation frame. Wheel and
// pointermove fire faster than the display refreshes; without this, several
// full static rebuilds happen between two frames and all but the last are
// thrown away.
//
// `analysis: true` means the ROAD changed, so the score and time table must be
// recomputed. View-only changes (zoom, pan) omit it: analyzeRoad(road, puzzle)
// is a pure function of road + terrain and returns the same result regardless
// of the view transform, so re-running it during navigation is pure waste.
let renderPending = false;
let renderNeedsAnalysis = false;

function scheduleRender(opts: { analysis?: boolean } = {}) {
  if (opts.analysis) renderNeedsAnalysis = true;
  if (renderPending) return;
  renderPending = true;
  requestAnimationFrame(() => {
    renderPending = false;
    if (renderNeedsAnalysis) {
      renderNeedsAnalysis = false;
      updatePanel();
      rebuildTimeTable();
    }
    redraw();
    // Keep the profile's viewport brackets in sync with pan/zoom. Cheap
    // (a few hundred line segments) and idempotent with the updatePanel
    // call above, so no extra dirty-tracking is warranted.
    drawProfile();
  });
}

function redraw() {
  if (!puzzle) {
    mainCtx.clearRect(0, 0, cssW, cssH);
    mainCtx.fillStyle = "rgba(241, 232, 211, 0.3)";
    mainCtx.fillRect(0, 0, cssW, cssH);
    return;
  }

  // --- Static layer: rebuild only when invalidated (puzzle load, resize,
  // pan, zoom). All the "doesn't change frame-to-frame" drawing goes here.
  if (staticDirty || !staticCanvas) {
    const dpr = window.devicePixelRatio || 1;
    if (!staticCanvas) staticCanvas = document.createElement("canvas");
    if (staticCanvas.width !== Math.round(cssW * dpr) ||
        staticCanvas.height !== Math.round(cssH * dpr)) {
      staticCanvas.width = Math.round(cssW * dpr);
      staticCanvas.height = Math.round(cssH * dpr);
    }
    const sctx = staticCanvas.getContext("2d")!;
    sctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    sctx.clearRect(0, 0, cssW, cssH);
    sctx.fillStyle = "rgba(241, 232, 211, 0.3)";
    sctx.fillRect(0, 0, cssW, cssH);
    // Redirect drawing to the static canvas for the composite.
    const savedCtx = ctx;
    ctx = sctx;
    try {
      drawRelief();
      drawRivers();
      drawLakes();
      if (DEBUG_GRID) drawGrid();
      drawSpillways();
      drawContours();
      drawTowns();
      drawExistingRoad();
      drawTownLabels();
    } finally {
      ctx = savedCtx;
    }
    staticDirty = false;
  }

  // --- Per-frame: blit the static composite, then draw dynamic layers.
  mainCtx.clearRect(0, 0, cssW, cssH);
  // The static canvas is in device pixels; blit at CSS size.
  mainCtx.drawImage(staticCanvas, 0, 0, cssW, cssH);
  drawProposedRoad();
  drawCars();
  drawHandles();
  // Debug overlays last so they sit on top of everything.
  if (DEBUG_SKELETON) drawSkeleton();
  if (DEBUG_ELEVATION) drawElevationProbes();
  if (DEBUG_DRAINAGE) drawDrainageLabels();
}

// Animation loop — continuously redraws to keep car positions current.
// Static analysis (lastAnalysis, lastTimeTable) is NOT recomputed here;
// it's updated only on road edits via draw().
//
// We pace to a maximum FPS regardless of display refresh rate. On a 120Hz
// or 144Hz monitor, raw requestAnimationFrame would burn CPU/GPU at that
// rate, which is wasteful for the visual fidelity we need.
const TARGET_FPS = 60;
const FRAME_INTERVAL_MS = 1000 / TARGET_FPS;
let animPending = false;
let lastFrameTime = 0;
function animationFrame() {
  animPending = false;
  if (!(SHOW_CARS && lastAnalysis && lastTripTime > 0)) return;
  const now = performance.now();
  if (now - lastFrameTime >= FRAME_INTERVAL_MS) {
    lastFrameTime = now;
    redraw();
  }
  animPending = true;
  requestAnimationFrame(animationFrame);
}
function ensureAnimating() {
  if (!animPending && SHOW_CARS) {
    animPending = true;
    requestAnimationFrame(animationFrame);
  }
}

function resizeCanvas() {
  const rect = canvas.getBoundingClientRect();
  cssW = Math.max(100, rect.width);
  cssH = Math.max(100, rect.height);
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);
  mainCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const margin = 30;
  const sx = (cssW - margin * 2) / MAP_W;
  const sy = (cssH - margin * 2) / MAP_H;
  fitScale = Math.min(sx, sy);
  fitOffsetX = (cssW - MAP_W * fitScale) / 2;
  fitOffsetY = (cssH - MAP_H * fitScale) / 2;
  // The limits are derived from fitOffset and cssW/H, all three of which just
  // changed, so a pan that was legal before the resize may not be now. The
  // previous code never re-clamped here and a shrink could strand the map
  // off-screen with no way back except Recenter.
  clampPanTo();
  invalidateStatic();
  draw();
}

// =============================================================
// UNDO HISTORY
// =============================================================
//
// Snapshots of `road`, newest last. A "move" is any completed edit to the
// road geometry: a finished anchor or handle drag, a fall-line snap (F),
// an insertion or removal, Reset, and the +/- Segment buttons.
//
// SNAPSHOT, NOT DIFF. Roads are small — a couple of dozen anchors with two
// handles each — so storing whole copies costs far less than the bookkeeping
// an inverse-operation log would need, and it cannot drift out of sync with
// the live object the way a replayed diff can.
//
// Each entry is the state BEFORE its move, so undo() is a plain pop-and-
// restore. Nothing is recorded for a move that left the geometry untouched:
// a click that starts a drag and releases without moving, or an F press on
// flat terrain, would otherwise fill the stack with entries whose undo does
// nothing visible — the classic "Ctrl+Z did nothing" bug.
//
// View state (pan, zoom) is deliberately NOT part of this. Undo restores what
// you built, not where you were looking.

/** Depth cap. Roads are small, but the stack must not grow without bound. */
const UNDO_LIMIT = 100;

let undoStack: Road[] = [];

/**
 * State captured when a drag begins, held until the drag ends.
 *
 * A drag mutates the road across many pointermove events; recording each one
 * would make a single gesture take dozens of Ctrl+Z presses to unwind. So the
 * snapshot is taken once at pointerdown and committed once at pointerup —
 * and only if the geometry actually changed.
 */
let dragUndoSnapshot: Road | null = null;

function cloneRoad(r: Road): Road {
  // Explicit deep copy rather than structuredClone: the shape is small and
  // fixed, and this keeps the cost obvious to anyone reading the drag path.
  return {
    anchors: r.anchors.map((p) => ({ x: p.x, y: p.y })),
    handles: r.handles.map((h) => ({
      in: h.in ? { x: h.in.x, y: h.in.y } : null,
      out: h.out ? { x: h.out.x, y: h.out.y } : null,
    })),
  };
}

/** Exact geometric equality. Used only to discard no-op moves. */
function sameRoad(a: Road, b: Road): boolean {
  if (a.anchors.length !== b.anchors.length) return false;
  for (let i = 0; i < a.anchors.length; i++) {
    if (a.anchors[i].x !== b.anchors[i].x || a.anchors[i].y !== b.anchors[i].y) return false;
    const ha = a.handles[i], hb = b.handles[i];
    if (!!ha.in !== !!hb.in || !!ha.out !== !!hb.out) return false;
    if (ha.in && hb.in && (ha.in.x !== hb.in.x || ha.in.y !== hb.in.y)) return false;
    if (ha.out && hb.out && (ha.out.x !== hb.out.x || ha.out.y !== hb.out.y)) return false;
  }
  return true;
}

function pushUndoState(prev: Road): void {
  undoStack.push(prev);
  if (undoStack.length > UNDO_LIMIT) undoStack.shift();
}

/**
 * Run an instantaneous road edit, recording it only if it changed anything.
 *
 * For everything except drags, which span multiple events and are handled by
 * the dragUndoSnapshot pair instead.
 */
function withUndo(fn: () => void): void {
  const before = cloneRoad(road);
  fn();
  if (!sameRoad(before, road)) pushUndoState(before);
}

/** Commit a drag's snapshot. Called on pointerup and when a pinch pre-empts
 *  the drag — in the latter case the road may already have moved, so the
 *  snapshot is committed rather than discarded. */
function commitDragUndo(): void {
  if (dragUndoSnapshot && !sameRoad(dragUndoSnapshot, road)) {
    pushUndoState(dragUndoSnapshot);
  }
  dragUndoSnapshot = null;
}

function undo(): void {
  const prev = undoStack.pop();
  if (!prev) return;
  road = prev;
  // Both name anchors by index, and undoing an insertion or removal shifts
  // every index after it. Clearing is correct rather than merely cautious:
  // a stale lastTouched would aim the next F press at a different anchor.
  lastTouched = null;
  drag = null;
  dragUndoSnapshot = null;
  draw();
}

/** Drop the history. Called on puzzle load — undoing across a change of map
 *  would restore a road built for different terrain and different towns. */
function clearUndo(): void {
  undoStack = [];
  dragUndoSnapshot = null;
}

// =============================================================
// INTERACTION
// =============================================================

let drag: any = null;
/**
 * The handle or anchor the user last grabbed, so a keyboard command has
 * something to act on after the pointer is released. Survives the drag
 * ending; cleared only when the road is rebuilt under it (reset, puzzle
 * load, or a removal that shifts indices).
 */
let lastTouched: { type: "in" | "out" | "anchor"; index: number } | null = null;
// Touch heuristic — used to enlarge hit radii. Stable enough for our purposes.
const IS_TOUCH = (typeof window !== "undefined") && ("ontouchstart" in window);
const HIT_RADIUS = IS_TOUCH ? 28 : 18;
const PATH_HIT_RADIUS = IS_TOUCH ? 20 : 12;

// Multi-pointer tracking. Map preserves insertion order, so iterating
// gives the "first finger down" and "second finger down" in that order.
const pointers = new Map<number, { x: number; y: number }>();
let pinch: {
  startDist: number;        // initial finger distance (screen px)
  startScale: number;       // viewScale at gesture start
  startWorldX: number;      // world coordinate under the centroid at gesture start
  startWorldY: number;
} | null = null;

// Long-press for anchor removal on touch (replaces right-click).
const LONG_PRESS_MS = 500;
const LONG_PRESS_MOVE_TOL = 6;  // any movement beyond this cancels the timer
let longPressTimer: number | null = null;
let longPressOrigin: { x: number; y: number } | null = null;

function getMousePos(e: MouseEvent | PointerEvent): Pt {
  const rect = canvas.getBoundingClientRect();
  return { x: e.clientX - rect.left, y: e.clientY - rect.top };
}
function hitTest(sx: number, sy: number) {
  for (let i = 0; i < road.anchors.length; i++) {
    const h = road.handles[i];
    if (h.in)  { const p = toScreen(h.in);  if (Math.hypot(p.x - sx, p.y - sy) < HIT_RADIUS) return { type: "in",  index: i }; }
    if (h.out) { const p = toScreen(h.out); if (Math.hypot(p.x - sx, p.y - sy) < HIT_RADIUS) return { type: "out", index: i }; }
  }
  for (let i = 1; i < road.anchors.length - 1; i++) {
    const p = toScreen(road.anchors[i]);
    if (Math.hypot(p.x - sx, p.y - sy) < HIT_RADIUS) return { type: "anchor", index: i };
  }
  return null;
}

/**
 * Nearest removable anchor within the hit radius, ignoring handles entirely.
 *
 * hitTest() searches handles before anchors, which is right for dragging —
 * a handle drawn on top of an anchor should be the thing you grab. It is
 * wrong for deletion: zoomed in, or with a short handle, the handle sits
 * within HIT_RADIUS of its own anchor, so a right-click on the anchor
 * returned {type:"in"} and the delete silently did nothing. There is no
 * "delete handle" action for that hit to mean, so the click was simply
 * swallowed.
 *
 * Nearest rather than first-within-radius, so with several anchors bunched
 * together at low zoom you remove the one you actually clicked.
 */
function hitTestAnchorOnly(sx: number, sy: number): { type: "anchor"; index: number } | null {
  let best = -1, bestD = HIT_RADIUS;
  for (let i = 1; i < road.anchors.length - 1; i++) {
    const p = toScreen(road.anchors[i]);
    const d = Math.hypot(p.x - sx, p.y - sy);
    if (d < bestD) { bestD = d; best = i; }
  }
  return best < 0 ? null : { type: "anchor", index: best };
}

/**
 * Terrain gradient at a world point, in normalized height per world unit.
 *
 * eps stays inside one bilinear patch (cells are 1 world unit), so this is
 * the exact gradient of the surface heightAt() actually interpolates rather
 * than a finite-difference approximation of it. Sampling wider blurs across
 * the cell boundary, where the bilinear gradient is genuinely discontinuous,
 * and measurably degrades the result: at eps 0.5 a contour-aligned step holds
 * 0.81% grade, at eps 1.5 it drifts to 4.85%.
 */
function terrainGradient(x: number, y: number): { gx: number; gy: number } {
  const eps = 0.5;
  const p = puzzle!.params;
  return {
    gx: (heightAt(x + eps, y, p) - heightAt(x - eps, y, p)) / (2 * eps),
    gy: (heightAt(x, y + eps, p) - heightAt(x, y - eps, p)) / (2 * eps),
  };
}

/**
 * Rotate the last-touched anchor's handles onto the contour — perpendicular
 * to the fall line — keeping their lengths.
 *
 * The handles set the curve's tangent at the anchor, so aligning them with
 * the contour makes the road locally level there. Measured across this map,
 * a contour-aligned step holds 0.81% grade against 4.0% down the fall line
 * over the same distance — 20x flatter.
 *
 * Worth knowing what this does NOT do: it is a tangent condition at one
 * point, not a contour-following road. The contour curves away from its own
 * tangent quickly on this terrain — by one world unit (500 m) the drift is
 * already ~4% — so a long handle will leave the contour well before it
 * reaches its far end. It sets the departure angle; it doesn't hold it.
 */
function snapToContour(): void {
  if (!puzzle || !lastTouched) {
    console.log("Snap: nothing touched yet — drag an anchor or handle first");
    return;
  }
  const i = lastTouched.index;
  if (i < 0 || i >= road.anchors.length) { lastTouched = null; return; }
  const a = road.anchors[i];
  const { gx, gy } = terrainGradient(a.x, a.y);
  const g = Math.hypot(gx, gy);
  if (g < 1e-9) {
    console.log("Snap: terrain is flat here — no fall line to be perpendicular to");
    return;
  }
  // Perpendicular to the fall line, i.e. along the contour.
  const cx = -gy / g, cy = gx / g;
  const h = road.handles[i];

  // An anchor grab has no side, so both handles move; a handle grab moves
  // that handle and lets G1 carry the other one.
  const lead: "in" | "out" | null = lastTouched.type === "anchor" ? null : lastTouched.type;
  const orient = (hp: Pt | null, fallback: number): number => {
    if (!hp) return fallback;
    const vx = hp.x - a.x, vy = hp.y - a.y;
    if (Math.hypot(vx, vy) < 1e-9) return fallback;
    // Keep whichever of the two contour directions the handle already points
    // nearer to, so the curve doesn't invert under the user.
    return (vx * cx + vy * cy) >= 0 ? 1 : -1;
  };
  const place = (hp: Pt | null, sign: number) => {
    if (!hp) return;
    const len = Math.hypot(hp.x - a.x, hp.y - a.y);
    if (len < 1e-9) return;
    hp.x = clamp(a.x + cx * sign * len, -20, MAP_W + 20);
    hp.y = clamp(a.y + cy * sign * len, -20, MAP_H + 20);
  };
  if (lead === null) {
    const sOut = orient(h.out, 1);
    place(h.out, sOut);
    place(h.in, -sOut);
  } else {
    const target = lead === "in" ? h.in : h.out;
    const other = lead === "in" ? h.out : h.in;
    const sT = orient(target, lead === "in" ? -1 : 1);
    place(target, sT);
    place(other, -sT);      // G1: the pair stays collinear through the anchor
  }
  const deg = (Math.atan2(cy, cx) * 180 / Math.PI).toFixed(0);
  console.log(`Snap: anchor ${i} handles set along the contour (${deg}deg)`);
  draw();
}

function cancelLongPress() {
  if (longPressTimer !== null) {
    clearTimeout(longPressTimer);
    longPressTimer = null;
  }
  longPressOrigin = null;
}

function removeAnchorIfRemovable(index: number) {
  if (index === 0 || index === road.anchors.length - 1) return false;
  pushUndoState(cloneRoad(road));
  road.anchors.splice(index, 1);
  road.handles.splice(index, 1);
  // Everything after `index` shifts down by one, so a stale lastTouched
  // would silently name a different anchor than the user last touched.
  if (lastTouched) {
    if (lastTouched.index === index) lastTouched = null;
    else if (lastTouched.index > index) lastTouched.index--;
  }
  draw();
  return true;
}

// Called when a second pointer arrives — switch from whatever single-finger
// gesture was active into pinch mode.
function startPinch() {
  if (pointers.size < 2) return;
  const [a, b] = [...pointers.values()];
  const cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;
  const dist = Math.hypot(b.x - a.x, b.y - a.y);
  const world = toWorld(cx, cy);
  pinch = {
    startDist: Math.max(1, dist),
    startScale: viewScale,
    startWorldX: world.x,
    startWorldY: world.y,
  };
  // Cancel any in-flight single-pointer gesture. Commit rather than discard:
  // the drag may already have moved the road before the second finger landed.
  commitDragUndo();
  drag = null;
  cancelLongPress();
  canvas.style.cursor = "grabbing";
}

function endPinch() {
  pinch = null;
}

canvas.addEventListener("pointerdown", (e) => {
  const m = getMousePos(e);
  pointers.set(e.pointerId, m);

  // Mouse right-click: anchor removal (desktop only; touch uses long-press).
  if (e.button === 2) {
    // Anchor-only: a handle lying over its anchor must not eat the delete.
    const hit = hitTestAnchorOnly(m.x, m.y);
    if (hit && removeAnchorIfRemovable(hit.index)) e.preventDefault();
    pointers.delete(e.pointerId);
    return;
  }

  // Second finger landed → switch to pinch.
  if (pointers.size === 2) {
    startPinch();
    e.preventDefault();
    return;
  }

  // Single-finger / mouse-button-1 gestures.
  const hit = hitTest(m.x, m.y);
  if (hit) {
    drag = hit;
    // Before the first pointermove, so the whole gesture undoes as one move.
    dragUndoSnapshot = cloneRoad(road);
    lastTouched = { type: hit.type as "in" | "out" | "anchor", index: hit.index };
    try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
    canvas.style.cursor = "grabbing";

    // Arm long-press for anchor removal (touch only — irrelevant for mouse,
    // but harmless since mouse drags will move and cancel it instantly).
    //
    // Armed from an anchor-only hit rather than from `hit`, for the same
    // reason right-click is: with the 28 px touch radius a handle very often
    // covers its own anchor, and keying off `hit` meant the grab landed on
    // the handle and long-press never armed. Restricted to the handle
    // belonging to THAT anchor, so holding still on some other anchor's
    // handle can't delete it out from under you. Any movement past
    // LONG_PRESS_MOVE_TOL still cancels, so a real handle drag is unaffected.
    if (e.pointerType !== "mouse") {
      const pressAnchor = hitTestAnchorOnly(m.x, m.y);
      if (pressAnchor && (hit.type === "anchor" || hit.index === pressAnchor.index)) {
        const victim = pressAnchor.index;
        longPressOrigin = { x: m.x, y: m.y };
        longPressTimer = window.setTimeout(() => {
          // Fire only if still alive and finger hasn't moved enough.
          if (longPressOrigin && drag && drag.index === victim) {
            removeAnchorIfRemovable(victim);
            drag = null;
          }
          longPressTimer = null;
        }, LONG_PRESS_MS);
      }
    }

    e.preventDefault();
    return;
  }

  const w = toWorld(m.x, m.y);
  const onPath = closestPointOnRoad(w);
  if (onPath && onPath.dist * curScale() < PATH_HIT_RADIUS) {
    // At the budget, a click on the road must not silently insert. Fall
    // through to pan rather than returning early, so the gesture still does
    // something sensible; refuse() flashes the header readout and the
    // sandbox toggle so the cause and the escape hatch are both visible.
    if (!cpCanAdd()) {
      cp()?.refuse();
    } else {
      // Captured before the insert: the new anchor is dragged immediately, so
      // one gesture both adds and positions it, and one undo should reverse
      // the whole thing rather than leaving a stray point behind.
      dragUndoSnapshot = cloneRoad(road);
      const newIdx = insertAnchorOnSegment(onPath.segIndex, onPath.t);
      drag = { type: "anchor", index: newIdx };
      lastTouched = { type: "anchor", index: newIdx };
      try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
      canvas.style.cursor = "grabbing"; draw(); e.preventDefault(); return;
    }
  }
  // Elevation-probe debug mode: a click on empty terrain drops a probe
  // rather than starting a pan. Pan is still available via two-finger
  // pinch and via dragging on the road / handles.
  if (DEBUG_ELEVATION && puzzle) {
    const wClamped = { x: clamp(w.x, 0, MAP_W), y: clamp(w.y, 0, MAP_H) };
    elevationProbes.push(wClamped);

    pointers.delete(e.pointerId);
    e.preventDefault();
    redraw();
    return;
  }
  drag = { type: "pan", startX: m.x, startY: m.y, origPanX: viewPanX, origPanY: viewPanY };
  try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
  canvas.style.cursor = "grabbing"; e.preventDefault();
});

canvas.addEventListener("contextmenu", (e) => e.preventDefault());

canvas.addEventListener("pointermove", (e) => {
  const m = getMousePos(e);
  if (pointers.has(e.pointerId)) pointers.set(e.pointerId, m);

  const w = toWorld(m.x, m.y);
  const coords = document.getElementById("coords");
  if (coords) coords.textContent = `${w.x.toFixed(1)}E · ${w.y.toFixed(1)}N`;

  // Pinch mode: read both fingers, compute scale + pan to keep centroid stable.
  if (pinch && pointers.size >= 2) {
    const [a, b] = [...pointers.values()];
    const cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;
    const dist = Math.hypot(b.x - a.x, b.y - a.y);
    const factor = dist / pinch.startDist;
    const newScale = clamp(pinch.startScale * factor, MIN_ZOOM, MAX_ZOOM);
    viewScale = newScale;
    // Re-anchor pan so the world coord initially under the centroid stays there.
    viewPanX = cx - fitOffsetX - pinch.startWorldX * fitScale * viewScale;
    viewPanY = cy - fitOffsetY - pinch.startWorldY * fitScale * viewScale;
    clampPan(); markNavigating(); scheduleRender();
    return;
  }

  // Cancel long-press if finger moved beyond tolerance.
  if (longPressOrigin && longPressTimer !== null) {
    const dx = m.x - longPressOrigin.x;
    const dy = m.y - longPressOrigin.y;
    if (Math.hypot(dx, dy) > LONG_PRESS_MOVE_TOL) cancelLongPress();
  }

  if (drag) {
    if (drag.type === "pan") {
      viewPanX = drag.origPanX + (m.x - drag.startX);
      viewPanY = drag.origPanY + (m.y - drag.startY);
      clampPan(); markNavigating(); scheduleRender(); return;
    }
    if (drag.type === "anchor") {
      const a = road.anchors[drag.index];
      const nx = clamp(w.x, 0, MAP_W), ny = clamp(w.y, 0, MAP_H);
      const dx = nx - a.x, dy = ny - a.y;
      a.x = nx; a.y = ny;
      const h = road.handles[drag.index];
      if (h.in)  { h.in.x  += dx; h.in.y  += dy; }
      if (h.out) { h.out.x += dx; h.out.y += dy; }
    } else {
      const h = road.handles[drag.index];
      const target = drag.type === "in" ? h.in! : h.out!;
      const anchorNow = road.anchors[drag.index];

      // Shift freezes the handle's direction: the drag then changes only its
      // length. Shift is the constrain-direction modifier nearly everywhere
      // (Illustrator, Figma, Inkscape); Ctrl is overloaded by the browser and
      // on macOS synthesises a right-click, which would fight the delete.
      //
      // The frozen angle is captured when Shift goes DOWN rather than at drag
      // start, so you can drag freely, decide the angle is right, then hold
      // Shift to pull the handle in or out along it. Releasing Shift drops
      // the lock and re-captures on the next press.
      if (e.shiftKey) {
        if (drag.lockTheta === undefined) {
          const vx = target.x - anchorNow.x, vy = target.y - anchorNow.y;
          drag.lockTheta = (Math.hypot(vx, vy) > 1e-6)
            ? Math.atan2(vy, vx)
            // Degenerate handle sitting on its anchor has no angle to keep;
            // fall back to the road's local direction there.
            : Math.atan2(
                (road.anchors[Math.min(drag.index + 1, road.anchors.length - 1)].y
                 - road.anchors[Math.max(drag.index - 1, 0)].y),
                (road.anchors[Math.min(drag.index + 1, road.anchors.length - 1)].x
                 - road.anchors[Math.max(drag.index - 1, 0)].x),
              ) + (drag.type === "in" ? Math.PI : 0);
        }
        const ux = Math.cos(drag.lockTheta), uy = Math.sin(drag.lockTheta);
        // Project the pointer onto the frozen ray. Clamped at a small
        // positive length so pulling back past the anchor shortens the
        // handle instead of flipping it through to the far side, which
        // would invert the curve without the angle ever visibly changing.
        const len = Math.max(0.05, (w.x - anchorNow.x) * ux + (w.y - anchorNow.y) * uy);
        target.x = clamp(anchorNow.x + ux * len, -20, MAP_W + 20);
        target.y = clamp(anchorNow.y + uy * len, -20, MAP_H + 20);
      } else {
        drag.lockTheta = undefined;
        target.x = clamp(w.x, -20, MAP_W + 20);
        target.y = clamp(w.y, -20, MAP_H + 20);
      }
      // G1 smoothing
      const anchor = road.anchors[drag.index];
      const opposite = drag.type === "in" ? h.out : h.in;
      if (opposite) {
        const dvx = target.x - anchor.x, dvy = target.y - anchor.y;
        const dLen = Math.hypot(dvx, dvy);
        if (dLen > 1e-6) {
          const ovx = opposite.x - anchor.x, ovy = opposite.y - anchor.y;
          const oLen = Math.hypot(ovx, ovy);
          if (oLen > 1e-6) {
            opposite.x = anchor.x + (-dvx / dLen) * oLen;
            opposite.y = anchor.y + (-dvy / dLen) * oLen;
          }
        }
      }
    }
    scheduleRender({analysis: true});
  } else {
    // Hover feedback only matters with a mouse — skip on touch.
    if (!IS_TOUCH) {
      const hit = hitTest(m.x, m.y);
      if (hit) { canvas.style.cursor = "grab"; }
      else {
        const onPath = closestPointOnRoad(w);
        const onRoad = !!(onPath && onPath.dist * curScale() < PATH_HIT_RADIUS);
        // "copy" promises an insert. At the budget there is no insert to
        // make, so say so rather than letting the cursor lie.
        canvas.style.cursor = !onRoad ? "crosshair"
                            : cpCanAdd() ? "copy"
                            : "not-allowed";
      }
    }
  }
});

function endPointer(e: PointerEvent) {
  pointers.delete(e.pointerId);
  cancelLongPress();
  // If we were pinching and we no longer have two fingers, leave pinch.
  if (pinch && pointers.size < 2) endPinch();
  // Single-finger drags end normally.
  if (drag) {
    try { canvas.releasePointerCapture(e.pointerId); } catch (_) {}
    drag = null;
  }
  commitDragUndo();
  canvas.style.cursor = IS_TOUCH ? "default" : "crosshair";
}
canvas.addEventListener("pointerup", endPointer);
canvas.addEventListener("pointercancel", endPointer);

canvas.addEventListener("wheel", (e) => {
  e.preventDefault();
  const m = getMousePos(e as any);
  const wBefore = toWorld(m.x, m.y);
  const factor = Math.exp(-e.deltaY * 0.0015);
  const newScale = clamp(viewScale * factor, MIN_ZOOM, MAX_ZOOM);
  if (newScale === viewScale) return;
  viewScale = newScale;
  viewPanX = m.x - fitOffsetX - wBefore.x * fitScale * viewScale;
  viewPanY = m.y - fitOffsetY - wBefore.y * fitScale * viewScale;
  clampPan(); markNavigating(); scheduleRender();
}, { passive: false });

// =============================================================
// PANEL UPDATES
// =============================================================

function updatePanel() {
  // Every path that mutates the road calls draw() or scheduleRender({analysis})
  // and so lands here: + / − Segment, click-to-insert, right-click and
  // long-press removal, Reset, and puzzle load. One call site covers them all.
  // Sits above the early returns so the readout stays truthful even when the
  // analysis bails.
  cp()?.setCount(controlPointCount());
  if (!puzzle) return;
  // Coarse while a drag is in flight, exact the moment it ends.
  //
  // analyzeRoad spends nearly all its time in solveProfileOptimal, whose
  // lattice is samples x 1600 elevation levels: ~217 ms on a 300x150 map
  // against under 5 ms for every other stage combined. At 60 fps that is
  // thirteen frames per pointermove, i.e. unusable.
  //
  // 400 levels costs ~44 ms here and lands within about 1% of exact.
  //
  // The error is NOT one-directional, contrary to what "the refinement is
  // only kept if it improves on the baseline" suggests: measured on a real
  // v2 map the coarse lattice came out 1.04% CHEAPER than the fine one. The
  // lattice optimises its own discretised objective, while the number shown
  // comes from scoreRoute over the resulting profile, and the two do not
  // agree exactly — so a coarser grid can land somewhere scoreRoute happens
  // to like better.
  //
  // That is a display wobble on release, not an exploit: /api/submit
  // re-scores server-side at full precision, so nothing is farmable by
  // dragging.
  lastAnalysis = analyzeRoad(road, puzzle, drag ? { maxLevels: 400 } : {});
  if (!lastAnalysis) return;
  drawProfile();
  const s = lastAnalysis.score;
  const $ = (id: string) => document.getElementById(id)!;
  $("infeasible-warn").style.display = lastAnalysis.feasible ? "none" : "block";
  $("m-length").innerHTML   = `${s.length.toFixed(1)} <span class="unit">km</span>`;
  $("m-time").innerHTML     = `${s.timeMin.toFixed(1)} <span class="unit">min</span>`;
  $("m-cost").innerHTML     = `${s.cost.toFixed(1)} <span class="unit">M$</span>`;
  $("m-minspeed").innerHTML = `${s.minSpeed.toFixed(0)} <span class="unit">km/h</span>`;

  renderTarget(s.cost, s.timeMin);

  // Mobile-strip mirror (only visible on narrow viewports, but always updated).
  const stripCost = document.getElementById("strip-cost");
  const stripTime = document.getElementById("strip-time");
  if (stripCost) stripCost.textContent = `${s.cost.toFixed(1)} M$`;
  if (stripTime) stripTime.textContent = `${s.timeMin.toFixed(1)} min`;

  // If the scatterplot modal is currently open, re-render it so the
  // user's live "current road" marker tracks every edit. Whole-SVG
  // re-render is fine here: it's sub-millisecond for typical submission
  // counts, and analyzeRoad above dominates per-edit cost anyway.
  const plotModal = document.getElementById("plot-modal");
  if (plotModal && !plotModal.hasAttribute("hidden")) renderScatterplot();

  if (DEBUG_GRADE_VIZ) {
    const { samples, z, kinds } = lastAnalysis;
    const v: any[] = [];
    for (let i = 1; i < samples.length; i++) {
      const ds = samples[i].s - samples[i - 1].s;
      if (ds < 1e-6) continue;
      const grade = (z[i] - z[i - 1]) / ds;
      if (Math.abs(grade) > MAX_GRADE + 1e-6) {
        v.push({ i, s: samples[i].s.toFixed(2), grade: (grade * 100).toFixed(2), kind: kinds[i - 1] });
      }
    }
    if (v.length > 0) {
      console.log(`[grade debug] ${v.length} segment(s) > ${MAX_GRADE * 100}%:`);
      for (const x of v) console.log(`  i=${x.i} s=${x.s}km grade=${x.grade}% kind=${x.kind}`);
    }
  }
}

// =============================================================
// ARCHIVE MODE (developer-only)
// =============================================================
// Load any past day with ?date=YYYY-MM-DD, or step through days with the
// [ and ] keys. Exists so consecutive days can be compared directly — the
// only reliable way to tell a generator that isn't varying from two days
// that happened to land similar.
//
// The banner is built here rather than in index.html deliberately: it's a
// debugging affordance, and markup that only ever appears under a query
// param is markup that rots. Nothing else in the page needs to know.

function renderArchiveBanner(): void {
  const existing = document.getElementById("archive-banner");
  if (!archiveMode) { existing?.remove(); return; }
  const el = existing ?? document.createElement("div");
  el.id = "archive-banner";
  el.setAttribute("style", [
    "position:fixed", "top:0", "left:0", "right:0", "z-index:9999",
    "padding:6px 12px", "background:#7a4a00", "color:#ffe9c2",
    "font:600 13px/1.4 system-ui,sans-serif", "text-align:center",
    "pointer-events:none",
  ].join(";"));
  el.textContent =
    `ARCHIVE · ${dateStr} · not today's puzzle — submissions and streaks are off` +
    ` · [ and ] step days`;
  if (!existing) document.body.appendChild(el);
}

/**
 * Reload on the puzzle `delta` days from the current one.
 *
 * A full page reload, not an in-place refetch: the puzzle load rebuilds the
 * relief canvas, town layouts and contour cache, and a
 * reload gets all of that for free with no chance of a stale fragment
 * surviving into the next day. It's a debug path; the round trip is fine.
 *
 * Stepping forward past the server's today drops the parameter entirely
 * rather than requesting a future date the API would reject, so ] walks you
 * back to the live puzzle and stops there.
 */
function stepPuzzleDate(delta: number): void {
  if (!dateStr) return;
  const t = Date.parse(`${dateStr}T00:00:00Z`);
  if (!Number.isFinite(t)) return;
  const next = new Date(t + delta * 86_400_000).toISOString().slice(0, 10);
  const url = new URL(location.href);
  if (serverToday && next >= serverToday) url.searchParams.delete("date");
  else url.searchParams.set("date", next);
  location.href = url.toString();
}

// =============================================================
// API: PUZZLE, SUBMIT, LEADERBOARD
// =============================================================

async function fetchPuzzle() {
  // Forward the page's own query params to the API: ?debug=true asks for the
  // per-cell drainage payload, ?date= asks for an archived day. Built up
  // rather than always sent, so an ordinary load still fetches a clean URL
  // and stays cacheable.
  //
  // (`debug` used to be a hardcoded `false` here, which quietly made the
  // D-key drainage overlay unreachable no matter what the page URL said.)
  const q = new URLSearchParams();
  if (DEBUG_PAYLOAD) q.set("debug", "true");
  if (REQUESTED_DATE) q.set("date", REQUESTED_DATE);
  const qs = q.toString();
  const res = await fetch("./api/puzzle/today" + (qs ? `?${qs}` : ""));
  if (!res.ok) {
    // The archive endpoint has real 400s and 404s (bad date, no blob for
    // that day) and they're worth reading rather than swallowing.
    const detail = await res.json().catch(() => null);
    throw new Error(detail?.error
      ? `could not load puzzle: ${detail.error}`
      : "could not load puzzle");
  }
  const data = await res.json();

  // Decode the heightmap. v1 blobs ship raw little-endian Float32 bytes; v2
  // quantises to Uint16 over [min, max], which halves the payload. The server
  // labels which one it sent rather than making us infer it from a version
  // number, and omits the label on v1 — so absent means f32.
  const heightmap = data.terrain.encoding === "u16"
    ? decodeHeightmapU16({
        b64: data.terrain.dataB64,
        min: data.terrain.min,
        max: data.terrain.max,
      })
    : decodeF32B64(data.terrain.dataB64);

  // The map dimensions are compiled into this bundle as MAP_W/MAP_H but they
  // also travel with the blob. A mismatch means the deployed client and the
  // served puzzle version disagree — every world coordinate would be wrong,
  // and silently so, since nothing else would throw. Say it out loud.
  if (data.terrain.width !== MAP_W || data.terrain.height !== MAP_H) {
    console.error(
      `[puzzle] dimension mismatch: server sent ${data.terrain.width}x${data.terrain.height}, ` +
      `this client is built for ${MAP_W}x${MAP_H}. The client bundle and ` +
      `PUZZLE_VERSION are out of step — redeploy, or roll the var back.`,
    );
  }

  const incomingRivers = (data.rivers as River[] | undefined) ?? [];
  const incomingLakes = (data.lakes as Lake[] | undefined) ?? [];

  const params: TerrainParams = {
    heightmap,
    width: data.terrain.width,
    height: data.terrain.height,
  };

  // Rivers and lakes are carried on the Puzzle so analyzeRoad can run
  // geometric crossing tests against them. They aren't water tiles —
  // isWater() still consults only the heightmap (for ocean).
  puzzle = {
    seed: data.seed,
    params,
    towns: data.towns,
    existingRoad: data.existingRoad,
    rivers: incomingRivers,
    lakes: incomingLakes,
  };
  skeleton = (data.skeleton as Skeleton | undefined) ?? null;
  // Par is computed at generation time and shipped with the blob. A day whose
  // blob predates that (or whose generator failed to find a valid target)
  // simply has no target: renderTarget() hides the row and the game is still
  // playable, just unscored against par.
  par = data.par && Number.isFinite(data.par.cost) && Number.isFinite(data.par.timeMin)
    ? { cost: data.par.cost, timeMin: data.par.timeMin }
    : null;
  rivers = incomingRivers;
  lakes = incomingLakes;
  // Drainage debug data is present only when ?debug=true was passed.
  drainageCells = (data.debug?.drainage as Array<{ x: number; y: number; d: number }> | undefined) ?? [];
  spillwayCells = (data.debug?.spillways as Array<{ x: number; y: number }> | undefined) ?? [];
  discovererCells = (data.debug?.discoverers as Array<{ x: number; y: number }> | undefined) ?? [];
  elevationProbes = [];   // discard probes from any prior puzzle
  dateStr = data.date;
  archiveMode = data.archive === true;
  serverToday = typeof data.today === "string" ? data.today : data.date;
  // Streak display depends on the server's puzzle date, so it can't render
  // until now. Safe to call before the leaderboard arrives: it reads only
  // localStorage.
  renderStreak();
  renderArchiveBanner();
  document.getElementById("puzzle-date")!.textContent = dateStr;
  document.getElementById("puzzle-subtitle")!.textContent =
    `${archiveMode ? `Archive · ${dateStr}` : "Today"} · ` +
    `${puzzle.towns[0].name} → ${puzzle.towns[1].name}`;
  HYPSO_STOPS = buildHypsoStops(puzzle.params);
  reliefCanvas = buildReliefCanvas(puzzle.params);
  await loadAllSprites();
  buildTownLayouts(puzzle);
  buildContourCache();
  road = defaultRoad(puzzle);
  clearUndo();
  invalidateStatic();
}

// =============================================================
// TARGET (PAR) — STATE & WIN ASSESSMENT
// =============================================================
// The figures for the existing road, computed at puzzle-generation time and
// sent with the puzzle. Kept in a module-level variable rather than hung off
// Puzzle so shared/terrain.ts doesn't need a type change for it.
//
// The client does NOT compute par. It used to, as a fallback when the blob
// carried none: a budgeted, yielded search of ~1000 analyzeRoad calls, cached
// in localStorage under `par:<date>`. That is gone. Par is now whatever the
// generator decided, identical for every player, and `null` means the day
// genuinely has no target rather than "not computed yet".
//
// The win condition is PARETO DOMINANCE, not a single-axis beat: no worse on
// either objective and strictly better on at least one. That's the only
// verdict that doesn't quietly declare one end of the frontier the "right"
// one — beating par on cost alone would make cheap-and-slow the official
// answer, which is exactly what having two axes is meant to avoid.
type Par = { cost: number; timeMin: number };
let par: Par | null = null;

/* Compare at the precision the panel prints. Judging on raw floats would let
 * a road that reads "44.1 vs 44.1" be ruled a win or a loss on a difference
 * the player cannot see anywhere in the UI. */
const r1 = (v: number) => Math.round(v * 10) / 10;

type ParetoState = "win" | "partial" | "behind";
/**
 * How far below the target's cost a road must come to count as a cost win,
 * as a fraction. The single source of truth: the assessor below tests against
 * it and the win copy renders it into {t}, so the number the player is told
 * to beat and the number they are actually judged against cannot drift apart.
 *
 * Time is still a plain comparison — a win is "faster AND this much cheaper",
 * which is what par_win_body says.
 *
 * Measured headroom when this was set, for whoever tunes it next: the cheapest
 * road the optimiser reaches was 35.4%, 12.8% and 10.9% below par on three
 * consecutive days. A threshold above roughly 10% is therefore not reachable
 * on every day, so expect days that cannot be won on cost until par placement
 * moves back to match (par.ts's targetMargin is the other half of this dial).
 * Raising it here alone makes the game harder by making some days impossible
 * rather than by making them demanding.
 */
export const WIN_COST_MARGIN = 0.30;

/** The threshold as a bare percentage magnitude, for {t}. */
function winCostMarginPct(): string {
  const pct = WIN_COST_MARGIN * 100;
  return window.I18N ? window.I18N.n(pct, 0) : String(Math.round(pct));
}

/**
 * instr_intro carries a {t} placeholder naming the cost margin needed to win.
 *
 * I18N.t() is a bare dictionary lookup — it does no interpolation — and the
 * generic [data-i18n] applier writes the result straight into textContent. So
 * without this the intro renders a literal "{t}%" to every player. Same
 * problem instr_10 has with {n}, solved the same way (see syncInstr in
 * ui/cpBudget.ts): re-substitute after each apply().
 *
 * Note {t} means something different here than in par_win_body. There it is
 * the improvement the player ACHIEVED; here it is the threshold they must
 * REACH. Both come from WIN_COST_MARGIN, so they cannot drift apart.
 */
// Published for ui/coach.ts, which shows instr_intro as its first teaching
// note and needs the same number. A global rather than an import because
// client.ts imports coach.ts, so the reverse direction would be circular.
(window as any).RB_WIN_MARGIN_PCT = Math.round(WIN_COST_MARGIN * 100);

function syncInstrIntro(): void {
  const el = document.querySelector('[data-i18n="instr_intro"]');
  if (!el || !window.I18N) return;
  el.textContent = window.I18N.t("instr_intro").replace("{t}", winCostMarginPct());
}
// apply() runs before setLocale dispatches 'localechange', so this listener
// always overwrites the freshly-applied (un-substituted) text, never precedes it.
document.addEventListener("localechange", syncInstrIntro);
syncInstrIntro();

function paretoState(): ParetoState | null {
  if (!par || !lastAnalysis || !lastAnalysis.feasible) return null;
  const cost = r1(lastAnalysis.score.cost), time = r1(lastAnalysis.score.timeMin);
  const pc = r1(par.cost), pt = r1(par.timeMin);
  // Cost must clear the margin; time only has to beat par.
  const costOK = cost <= r1(pc * (1 - WIN_COST_MARGIN)), timeOK = time <= pt;
  if (costOK && timeOK) return "win";
  return (costOK || timeOK) ? "partial" : "behind";
}

// =============================================================
// TARGET RENDERING
// =============================================================

/**
 * Look up a UI string.
 *
 * The host dictionary (defined inline in index.html) stays authoritative, but
 * it is not the only source any more: PAR_STRINGS carries the par/win copy in
 * seven locales and backs the host up. Two cases it covers —
 *
 *   No helper at all. This used to return the KEY, so a page that loaded
 *   before the inline script, or with it removed, rendered the literal text
 *   "par_status_win" into the status line.
 *
 *   Helper present but missing this key. The convention for a miss is to
 *   echo the key back, so the same literal leaks through. Treating a response
 *   equal to the key as a miss lets the local table answer instead.
 *
 * Anything outside PAR_STRINGS still falls through to the key, unchanged.
 */
function t(key: string): string {
  const hosted = window.I18N?.t(key);
  if (hosted && hosted !== key) return hosted;
  const tag = window.I18N?.locale
    ?? (typeof navigator !== "undefined" ? navigator.language : undefined)
    ?? "en";
  const k = key as keyof ParStrings;
  return PAR_STRINGS[resolveLocale(tag)]?.[k] ?? PAR_STRINGS.en[k] ?? key;
}

/** Signed, fixed to one decimal: "-3.4" / "+1.2". Lower is better on both axes. */
function fmtDelta(mine: number, target: number): { text: string; better: boolean } {
  const d = r1(mine) - r1(target);
  return { text: (d > 0 ? "+" : d < 0 ? "\u2212" : "\u00b1") + Math.abs(d).toFixed(1),
           better: d < 0 };
}

function renderTarget(cost: number, timeMin: number) {
  const row = document.getElementById("target-row");
  const status = document.getElementById("pareto-status");
  const dCost = document.getElementById("d-cost");
  const dTime = document.getElementById("d-time");
  if (!row || !status || !dCost || !dTime) return;

  // No par from the server: leave the panel exactly as it was before.
  if (!par) {
    row.hidden = true; status.hidden = true;
    dCost.textContent = ""; dTime.textContent = "";
    return;
  }

  row.hidden = false;
  const cost_tgt = document.getElementById("m-target-cost");
  const fast_tgt = document.getElementById("m-target-speed");
  if (cost_tgt) cost_tgt.textContent = `${((1 - WIN_COST_MARGIN) * par.cost).toFixed(1)} M$`;
  if (fast_tgt) fast_tgt.textContent = `${par.timeMin.toFixed(1)} min`;

  const dc = fmtDelta(cost,(1 - WIN_COST_MARGIN) * par.cost), dt = fmtDelta(timeMin, par.timeMin);
  dCost.textContent = dc.text;
  dTime.textContent = dt.text;
  dCost.className = "delta " + (dc.better ? "better" : "worse");
  dTime.className = "delta " + (dt.better ? "better" : "worse");

  const state = paretoState();
  if (!state) { status.hidden = true; return; }
  status.hidden = false;
  status.className = state === "win" ? "win" : state === "partial" ? "partial" : "";
  let textData = state === "win" ? "par_status_win"
    : state === "partial" ? "par_status_partial"
    : "par_status_behind";
  status.textContent = t(textData);
  status.setAttribute("data-i18n", textData);

  if (state === "win") maybeCelebrate(dc.text, winCostMarginPct());
}

/**
 * Fires the win modal at most once per puzzle date.
 *
 * Gated on CPBudget.qualifies() as well as the Pareto test: a sandbox road
 * with thirty control points can dominate almost any target, and letting that
 * pop the celebration would make the budget rule meaningless.
 */
/**
 * `costPct` fills {t}, which is not a typo but is a trap worth naming.
 *
 * The copy reads "...and has a {t}% lower cost", so {t} is a COST figure
 * despite the letter. This used to be wired to the time delta, which meant
 * the modal printed the time saving into the cost sentence, with a literal
 * "%" stuck onto a value in minutes and a leading minus sign — "a −4.5%
 * lower cost". {c} was never referenced by the string at all, so the cost
 * delta was computed and dropped.
 *
 * It now carries WIN_COST_MARGIN — the threshold the road just cleared, not
 * the amount it cleared it by. So a road 45% under par still reads "30%".
 * That is the cost of keeping copy and assessor on one constant; if you want
 * the achieved figure instead, it has to come from somewhere that also knows
 * the threshold, or the two go out of sync again.
 *
 * Renaming the placeholder to something like {costPct} would be clearer, but
 * that has to happen in the same commit as every translated string, so it is
 * left alone here rather than half-done.
 */
function maybeCelebrate(costDelta: string, costPct: string) {
  const budget = cp();
  if (budget && !budget.qualifies()) return;

  const key = `parWin:${dateStr}`;
  try { if (localStorage.getItem(key)) return; localStorage.setItem(key, "1"); }
  catch (_) { /* private mode: celebrate every time rather than not at all */ }

  const modal = document.getElementById("win-modal");
  const body = document.getElementById("win-modal-body");
  if (!modal || !body) return;
  body.textContent = t("par_win_body")
    .replace("{c}", costDelta)
    .replace("{t}", costPct);
  winModalReturnFocus = document.activeElement as HTMLElement | null;
  modal.removeAttribute("hidden");
  // Focus the dismiss button, not the panel: Enter or Space then closes it,
  // and a keyboard user isn't left hunting for the way out.
  document.getElementById("win-modal-close")?.focus();
}

let winModalReturnFocus: HTMLElement | null = null;

function closeWinModal() {
  const modal = document.getElementById("win-modal");
  if (!modal || modal.hasAttribute("hidden")) return;
  modal.setAttribute("hidden", "");
  winModalReturnFocus?.focus?.();
  winModalReturnFocus = null;
}

// =============================================================
// FRONTIER STREAK  (client-local, best-effort)
// =============================================================
//
// A "day earned" means: at some observed moment during that puzzle's day,
// one of your submissions was on the Pareto frontier. Once earned, a day is
// never revoked — this is deliberate, and it's also the only semantics the
// client can actually support. Frontier membership is monotonically
// *decreasing* for a fixed point (new submissions can only ever knock you
// off, never put you back on), so an end-of-day check would require the
// player to be sitting on the page at midnight. "Earned at any observed
// moment" is both the fairer rule and the implementable one.
//
// Days are keyed by the *server's* puzzle date (`dateStr`), never by the
// client clock: no timezone skew, no midnight-rollover ambiguity, and no
// credit from setting the system clock forward.
//
// This is a personal achievement counter with no server component, so a
// player who edits localStorage is only fooling themselves.

const STREAK_KEY = "roadbuilder.frontierStreak.v1";

type StreakRecord = {
  v: 1;
  last: string;     // puzzle date (YYYY-MM-DD) most recently earned
  current: number;  // consecutive earned days ending at `last`
  best: number;     // longest `current` ever reached
};

// Days since the Unix epoch for a YYYY-MM-DD string, or null if malformed.
// Fixed to UTC on purpose: local-time date math breaks across DST
// transitions (a "day" can be 23 or 25 hours, so ms/86400000 misrounds).
function toEpochDay(date: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return null;
  const y = +m[1], mo = +m[2], d = +m[3];
  const t = Date.UTC(y, mo - 1, d);
  // Date.UTC silently rolls over impossible dates (2025-02-30 -> Mar 2),
  // so round-trip to reject them rather than credit a phantom day.
  const back = new Date(t);
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d) {
    return null;
  }
  return Math.floor(t / 86_400_000);
}

// localStorage throws, not just returns null, in a few real situations:
// Safari private browsing, cookies-blocked, and quota exhaustion. Every
// access is guarded so a storage failure degrades to "no streak shown"
// instead of taking down the submit path or the render loop.
function readStreak(): StreakRecord | null {
  try {
    const raw = localStorage.getItem(STREAK_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p || p.v !== 1 || typeof p.last !== "string") return null;
    if (!Number.isFinite(p.current) || !Number.isFinite(p.best)) return null;
    if (toEpochDay(p.last) === null) return null;
    return { v: 1, last: p.last, current: Math.max(0, p.current | 0), best: Math.max(0, p.best | 0) };
  } catch {
    return null;   // absent, disabled, or corrupt — all handled the same way
  }
}

function writeStreak(r: StreakRecord): void {
  try {
    localStorage.setItem(STREAK_KEY, JSON.stringify(r));
  } catch {
    /* storage full or unavailable; the counter just won't persist */
  }
}

// Credit `date` to the streak. Returns true only on the transition from
// unearned to earned, so callers can fire a toast without spamming it on
// every 30s leaderboard poll.
//
// Always re-reads storage rather than trusting an in-memory copy: two open
// tabs both polling the leaderboard would otherwise double-count a day.
function recordFrontierDay(date: string): boolean {
  const day = toEpochDay(date);
  if (day === null) return false;

  const prev = readStreak();
  if (prev && prev.last === date) return false;   // already credited today

  let current = 1;
  if (prev) {
    const lastDay = toEpochDay(prev.last);
    if (lastDay !== null) {
      // Stored date at or after the puzzle date: clock skew, or an archived
      // puzzle being replayed. Leave the record untouched rather than
      // rewriting history backwards.
      if (lastDay >= day) return false;
      current = day - lastDay === 1 ? prev.current + 1 : 1;
    }
  }

  writeStreak({ v: 1, last: date, current, best: Math.max(current, prev?.best ?? 0) });
  return true;
}

// The displayable streak *right now*. Computed on read rather than stored,
// so it stays correct with no end-of-day job and no rollover timer: a
// streak whose last earned day was yesterday is still alive (today is
// simply not played yet); anything older is broken.
function liveStreak(today: string): { current: number; best: number; earnedToday: boolean } {
  const r = readStreak();
  if (!r) return { current: 0, best: 0, earnedToday: false };
  const t = toEpochDay(today), l = toEpochDay(r.last);
  if (t === null || l === null) return { current: 0, best: r.best, earnedToday: false };
  const gap = t - l;
  return {
    current: gap === 0 || gap === 1 ? r.current : 0,
    best: r.best,
    earnedToday: gap === 0,
  };
}

function renderStreak(): void {
  const el = document.getElementById("streak");
  if (!el) return;                      // no-op until the markup exists
  if (archiveMode) {
    // liveStreak() measures the gap to *this* puzzle's date, so on an
    // archived day it would report a streak that means nothing.
    el.innerHTML = `<span class="streak-hint">Streak paused — viewing an archived puzzle.</span>`;
    return;
  }
  const { current, best, earnedToday } = liveStreak(dateStr);
  if (current === 0 && best === 0) {
    el.innerHTML = `<span class="streak-hint">Land on the Pareto frontier to start a streak.</span>`;
    return;
  }
  const flame = earnedToday ? "🔥" : "·";
  const todayNote = earnedToday ? "frontier today" : "not on the frontier yet today";
  el.innerHTML = `
    <span class="streak-count">${flame} ${current} day${current === 1 ? "" : "s"}</span>
    <span class="streak-sub">${todayNote} · best ${best}</span>`;
}

function resetStreak(): void {
  try { localStorage.removeItem(STREAK_KEY); } catch { /* nothing to do */ }
  renderStreak();
}

// Award the day if any entry matching the current handle sits on the
// frontier. Called on every leaderboard refresh as a safety net; the
// authoritative signal is the server's `onFrontier` flag at submit time.
function checkFrontierForStreak(): void {
  // Archived replays never touch the streak. recordFrontierDay already
  // refuses to rewrite history backwards, but it would happily *start* a
  // streak on a date from three weeks ago if there were no record yet.
  if (!dateStr || archiveMode) return;
  const handleInput = document.getElementById("handle") as HTMLInputElement | null;
  const you = (handleInput?.value ?? "").trim().toLowerCase();
  if (!you) return;                     // no identity to match against

  const frontier = computeFrontier(cachedLeaderboard);
  for (const e of frontier) {
    if (e.handle.trim().toLowerCase() === you) {
      if (recordFrontierDay(dateStr)) renderStreak();
      return;
    }
  }
}

async function submitScore() {
  if (!puzzle || !lastAnalysis) return;
  const handleInput = document.getElementById("handle") as HTMLInputElement;
  const handle = handleInput.value.trim();
  const resultDiv = document.getElementById("submit-result")!;
  if (!handle) {
    resultDiv.innerHTML = `<div class="submit-result err">Enter a handle first.</div>`;
    return;
  }
  if (!lastAnalysis.feasible) {
    resultDiv.innerHTML = `<div class="submit-result err">Cannot submit infeasible route.</div>`;
    return;
  }
  // The server already refuses any date but today's, so this is only about
  // giving a comprehensible message instead of a bare 400.
  if (archiveMode) {
    resultDiv.innerHTML = `<div class="submit-result err">Archived puzzle — only today's date accepts submissions.</div>`;
    return;
  }
  // Sandbox on, or over the control-point budget. The button is already
  // disabled and the click intercepted in index.html; this is the backstop
  // for any programmatic path.
  const budget = cp();
  if (budget && !budget.qualifies()) {
    const msg = budget.sandbox
      ? (window.I18N ? window.I18N.t("cp_no_submit") : "Sandbox run — not eligible.")
      : `Over the ${budget.LIMIT}-control-point limit.`;
    resultDiv.innerHTML = `<div class="submit-result err">${msg}</div>`;
    return;
  }
  const btn = document.getElementById("btn-submit") as HTMLButtonElement;
  btn.disabled = true;
  btn.textContent = "Submitting…";
  try {
    const res = await fetch("./api/submit", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ date: dateStr, handle, road }),
    });
    const data = await res.json();
    if (!res.ok) {
      resultDiv.innerHTML = `<div class="submit-result err">${data.error ?? "Submission failed"}</div>`;
    } else {
      // Server-authoritative, and evaluated at the single best moment for
      // this entry: frontier membership only erodes from here as others
      // submit. Credit the streak off this rather than the polled snapshot.
      const extended = data.onFrontier ? recordFrontierDay(dateStr) : false;
      renderStreak();

      const onF = data.onFrontier ? " · On Pareto frontier!" : "";
      const streakNote = extended
        ? `<br>Frontier streak: ${liveStreak(dateStr).current} days 🔥`
        : "";
      resultDiv.innerHTML = `<div class="submit-result">
        Submitted. Cost ${data.score.cost.toFixed(1)} M$ · ${data.score.timeMin.toFixed(1)} min<br>
        your entry dominated by ${data.dominated} others ${onF}${streakNote}
      </div>`;
      await refreshLeaderboard();
    }
  } catch (err) {
    resultDiv.innerHTML = `<div class="submit-result err">Network error</div>`;
  } finally {
    // Not an unconditional `false`: the player can flip sandbox on while the
    // request is in flight, and this would otherwise hand them back an
    // enabled button for a run that no longer qualifies.
    btn.disabled = budget ? !budget.qualifies() : false;
    btn.textContent = "Submit Score";
  }
}

// Leaderboard data is fetched here and cached on the module level so the
// scatterplot can read from it without re-fetching. The polling already
// keeps it fresh every 30s; the modal just renders whatever's current.
type LeaderboardEntry = { handle: string; cost: number; timeMin: number };
let cachedLeaderboard: LeaderboardEntry[] = [];

// Single source of truth for frontier membership, shared by the scatterplot
// and the streak check. These were separate before; if they drift, a player
// can see a green frontier dot with their handle on it while the streak
// silently disagrees, which is the kind of bug nobody reports and everybody
// resents.
//
// Returns the frontier points in ascending-cost (== descending-time) order,
// which is also the order the stepped connector line wants to be drawn in.
//
// NOTE ON TIES: strict `<` means that among entries with identical (cost,
// time), only the first survives, and an entry matching a frontier point's
// cost with a worse time is excluded. That is the standard Pareto-optimality
// definition, but it is *not* what the old comment claimed ("no other
// submission has both lower cost AND lower time" would admit exact
// duplicates). Worth a decision: an exact tie with the frontier currently
// earns no streak credit for the later submitter.
function computeFrontier(entries: LeaderboardEntry[]): LeaderboardEntry[] {
  const sorted = entries
    .filter(e => Number.isFinite(e.cost) && Number.isFinite(e.timeMin))
    .sort((a, b) => a.cost - b.cost || a.timeMin - b.timeMin);
  const frontier: LeaderboardEntry[] = [];
  let bestTime = Infinity;
  for (const e of sorted) {
    if (e.timeMin < bestTime) {
      frontier.push(e);
      bestTime = e.timeMin;
    }
  }
  return frontier;
}

async function refreshLeaderboard() {
  const board = document.getElementById("leaderboard")!;
  try {
    // The endpoint already took a ?date=; in archive mode we point it at the
    // day being viewed so the scatterplot matches the map on screen.
    const res = await fetch("./api/leaderboard" +
      (archiveMode && dateStr ? `?date=${encodeURIComponent(dateStr)}` : ""));
    const data = await res.json();
    cachedLeaderboard = Array.isArray(data.top) ? data.top : [];
    if (data.count === 0) {
      board.innerHTML = `<div style="color: var(--ink-soft); font-style: italic;">No submissions yet.</div>`;
    } else {
      const rows = data.top.slice(0, 15).map((e: any, i: number) =>
        `<div class="row">
          <span class="rank">${i + 1}.</span>
          <span class="handle">${escapeHtml(e.handle)}</span>
          <span class="cost">${e.cost.toFixed(0)} M$</span>
          <span class="time">${e.timeMin.toFixed(0)} min</span>
        </div>`
      ).join("");
      board.innerHTML = rows;
    }
    // Safety net: catches a frontier landing the submit response missed —
    // e.g. the player submitted in an earlier session before this feature
    // shipped, or the submit response was lost to a network error after the
    // server had already recorded the entry.
    checkFrontierForStreak();

    // If the scatterplot modal is currently open, re-render it so it
    // reflects the new data (e.g. after the 30s poll or after submit).
    const modal = document.getElementById("plot-modal");
    if (modal && !modal.hasAttribute("hidden")) renderScatterplot();
  } catch (err) {
    board.innerHTML = `<div style="color: var(--accent);">Could not load.</div>`;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

//
// Pareto frontier is computed client-side: a submission is on the
// frontier if no other submission is strictly better on both axes.
// Frontier points are highlighted and connected with a stepped line.
//
// "Your" submissions are matched by handle (case-insensitive, trimmed)
// against the current value of the handle input. If the input is empty,
// no highlighting fires; this is intentional — we don't track identity
// any other way.

// Chart geometry in SVG viewBox units. Width and height chosen to match
// the 4:3 aspect ratio set in CSS. Margins leave room for axis labels.
const PLOT_W = 640;
const PLOT_H = 480;
// Margins are chosen so the plot area is an exact whole number of grid
// squares — 560 × 400. Nothing depends on that being a whole number of
// grid cells any more; the ruling is fitted to the data below rather than
// the other way round. The bottom margin is a drafting-sheet title band
// holding the x labels, the axis caption and the title block. Keeping the
// title block down here rather than inside the ruled area means it can
// never cover a point.
const PLOT_MARGIN = { top: 20, right: 16, bottom: 60, left: 64 };
// How many major divisions an axis may be ruled into. A wide window means
// there is nearly always a round step available without touching the
// scale, which is the whole point — the sheet adapts to the data, the
// data is never stretched to suit the sheet.
const GRID_COLS = 14;
const GRID_ROWS = 10;
// Fine rules per major cell — the "every fifth line is heavy" of real
// engineering paper.
const MINOR_PER_MAJOR = 5;
// A major cell is worth a power of ten over one of these. The awkward one
// is 3: it makes the step itself non-integer (100/3), which is why only
// every third rule gets labelled when it wins.
const STEP_DIVISORS = [1, 2, 3, 4, 5];

type AxisPlan = {
  min: number;
  max: number;
  step: number;        // value per major cell
  divisions: number;   // cells across this axis
  labelEvery: number;  // label every Nth rule
};

/** Candidate cell values near `near`, ascending, spanning a few decades. */
function stepLadder(near: number): { step: number; divisor: number }[] {
  const out: { step: number; divisor: number }[] = [];
  const k0 = Math.floor(Math.log10(Math.max(near, 1e-12))) - 1;
  for (let k = k0; k <= k0 + 4; k++) {
    const p = Math.pow(10, k);
    for (const d of STEP_DIVISORS) out.push({ step: p / d, divisor: d });
  }
  return out.sort((a, b) => a.step - b.step);
}

/**
 * Choose the value per major cell, and where the axis starts.
 *
 * The cell count is FIXED (14 across, 10 up) so the cells stay square —
 * 560/14 and 400/10 are both 40px. That means the scale can't be fitted to
 * the data continuously; instead the step is chosen off a round ladder and
 * the window is centred on the data:
 *
 *   1. snap the observed lo/hi to a coarse unit, so the axis doesn't twitch
 *      every time a submission nudges the extremes by a few units;
 *   2. take the smallest ladder step where divisions x step covers that
 *      range;
 *   3. centre the window on the midpoint, rounded to a whole label step,
 *      and back off half the divisions to find the low edge.
 */
function planAxis(dataLo: number, dataHi: number, divisions: number): AxisPlan {
  const span = Math.max(dataHi - dataLo, 1e-9);
  const roundUnit = Math.pow(10, Math.floor(Math.log10(span))) / 10;
  const lo = Math.round(dataLo / roundUnit) * roundUnit;
  const hi = Math.round(dataHi / roundUnit) * roundUnit;
  const range = Math.max(hi - lo, roundUnit);
  const mid = (lo + hi) / 2;

  for (const cand of stepLadder(range / divisions)) {
    const window = cand.step * divisions;
    if (window < range - 1e-9) continue;
    // The real data has to fit, not the rounded range — rounding lo upward
    // (450 -> 500) can otherwise put the low edge above the lowest point.
    if (window < dataHi - dataLo - 1e-9) continue;
    const labelEvery = cand.divisor === 3 ? 3 : 1;
    const labelStep = cand.step * labelEvery;
    const centre = Math.round(mid / labelStep) * labelStep;

    // Centre the window, then slide it by whole cells if that leaves a
    // point outside. Sliding preserves both the step and the label phase,
    // so it costs nothing — stepping up the ladder instead would throw
    // away up to half the sheet, since consecutive steps can differ by 2x.
    const lowest = dataHi - window;   // lowest edge that still covers the top
    const highest = dataLo;           // highest edge that still covers the bottom
    let min = centre - (divisions / 2) * cand.step;
    if (min > highest) min = Math.floor(highest / cand.step) * cand.step;
    if (min < lowest) min = Math.ceil(lowest / cand.step) * cand.step;
    // Costs and times are never negative, so a window running below zero is
    // wasted sheet. Pull it up if that still covers everything.
    if (dataLo >= 0 && min < 0 && lowest <= 0) min = 0;
    if (min > dataLo + 1e-9 || min + window < dataHi - 1e-9) continue;
    return { min, max: min + window, step: cand.step, divisions, labelEvery };
  }
  // Nothing on the ladder fits (degenerate or absurd span): degrade to an
  // unrounded step rather than drawing nothing.
  const step = span / divisions;
  return { min: dataLo, max: dataLo + step * divisions, step, divisions, labelEvery: 1 };
}

/** Major-rule values on this axis that should carry a label. */
function labelledTicks(p: AxisPlan, labelEvery: number): number[] {
  const labelStep = p.step * labelEvery;
  const out: number[] = [];
  for (let i = 0; i <= p.divisions; i++) {
    const v = p.min + i * p.step;
    if (Math.abs(v - Math.round(v / labelStep) * labelStep) < labelStep * 1e-6) out.push(v);
  }
  return out;
}

/** Decimal places needed to print tick values at this step without lying. */
function tickDecimals(step: number): number {
  return Math.max(0, Math.min(4, -Math.floor(Math.log10(step) + 1e-9)));
}

function renderScatterplot() {
  // getElementById is typed to return HTMLElement, which doesn't overlap with
  // SVGSVGElement, so the direct cast is rejected. querySelector resolves it
  // properly instead of routing through `as unknown as`.
  const svg = document.querySelector<SVGSVGElement>("svg#plot-svg");
  if (!svg) return;
  // Empty state: no submissions yet.
  if (cachedLeaderboard.length === 0) {
    svg.setAttribute("viewBox", `0 0 ${PLOT_W} ${PLOT_H}`);
    svg.innerHTML = `<text class="empty-text" x="${PLOT_W / 2}" y="${PLOT_H / 2}"
      text-anchor="middle">No submissions yet</text>`;
    return;
  }

  // Aggregate every point we want to render so we can compute axis bounds.
  // Order: existing first, then submissions (so submissions sit visually
  // on top of the existing-road marker in case of overlap).
  const handleInput = document.getElementById("handle") as HTMLInputElement | null;
  const youHandle = (handleInput?.value ?? "").trim().toLowerCase();

  type Pt = { x: number; y: number; label: string; cost: number; timeMin: number;
              kind: "existing" | "pareto" | "you" | "norm" | "current" };
  // NB: no `.map(e => ({...e}))` copy here any more — computeFrontier returns
  // references drawn from this same array, and the `frontierSet.has(e)` test
  // below is by identity. Cloning would break that lookup.
  const subs = cachedLeaderboard
    .filter(e => Number.isFinite(e.cost) && Number.isFinite(e.timeMin));

  const frontierSeq = computeFrontier(subs);
  // Identity lookup back to original entries (frontier flag and youness).
  const frontierSet = new Set<LeaderboardEntry>(frontierSeq);

  const points: Pt[] = [];
  for (const e of subs) {
    let kind: Pt["kind"] = "norm";
    if (frontierSet.has(e)) kind = "pareto";
    if (youHandle && e.handle.trim().toLowerCase() === youHandle) kind = "you";
    points.push({
      x: e.cost, y: e.timeMin, label: e.handle,
      cost: e.cost, timeMin: e.timeMin, kind,
    });
  }
  // Current in-progress road, if we have a valid analysis. This is the
  // dot the player should be watching — it moves as they edit. Drawn last
  // (on top of everything else) via the kind-ordering below.
  if (lastAnalysis) {
    const cs = lastAnalysis.score;
    if (Number.isFinite(cs.cost) && Number.isFinite(cs.timeMin)) {
      points.push({
        x: cs.cost, y: cs.timeMin, label: "Your current road",
        cost: cs.cost, timeMin: cs.timeMin, kind: "current",
      });
    }
  }

  // Axis bounds. Add ~6% padding around the data so points don't sit
  // exactly on the plot edges. Force inclusion of zero if data is close.
  const xs = points.map(p => p.x);
  const ys = points.map(p => p.y);
  let xMin = Math.min(...xs), xMax = Math.max(...xs);
  let yMin = Math.min(...ys), yMax = Math.max(...ys);
  // Degenerate-range fallback (one point or all identical): expand artificially.
  if (xMax - xMin < 1e-6) { xMin -= 1; xMax += 1; }
  if (yMax - yMin < 1e-6) { yMin -= 1; yMax += 1; }
  // No percentage padding here any more. planAxis rounds the observed
  // extremes to a coarse unit and then centres a whole number of cells on
  // the midpoint, which supplies the breathing room — and an arbitrary 4%
  // nudge beforehand would only perturb that rounding.

  const plotL = PLOT_MARGIN.left, plotR = PLOT_W - PLOT_MARGIN.right;
  const plotT = PLOT_MARGIN.top, plotB = PLOT_H - PLOT_MARGIN.bottom;

  // The range is now final — nothing below widens it. The ruling is fitted
  // to the data instead of the data being snapped to the ruling.
  const ruling = { x: planAxis(xMin, xMax, GRID_COLS), y: planAxis(yMin, yMax, GRID_ROWS) };
  const xR = ruling.x, yR = ruling.y;
  xMin = xR.min; xMax = xR.max;
  yMin = yR.min; yMax = yR.max;
  const cellW = (plotR - plotL) / GRID_COLS, cellH = (plotB - plotT) / GRID_ROWS;
  // Widen the x label interval if the numbers would collide — five-figure
  // costs don't fit in a 40px cell. Doubling keeps the labels on round
  // values, since 2 x a round step is still round. The y labels are stacked
  // vertically against a 40px cell, so they never crowd.
  let xLabelEvery = xR.labelEvery;
  for (let guard = 0; guard < 4; guard++) {
    const dp = tickDecimals(xR.step * xLabelEvery);
    const widest = Math.max(...labelledTicks(xR, xLabelEvery).map(v => v.toFixed(dp).length));
    if (widest * 6.6 + 6 <= cellW * xLabelEvery) break;
    xLabelEvery *= 2;
  }
  const xTicks = labelledTicks(xR, xLabelEvery), yTicks = labelledTicks(yR, yR.labelEvery);
  const xDp = tickDecimals(xR.step * xLabelEvery), yDp = tickDecimals(yR.step * yR.labelEvery);

  const sx = (v: number) => plotL + ((v - xMin) / (xMax - xMin)) * (plotR - plotL);
  // Y inverted: larger time sits higher up the sheet, so the cheap-and-fast
  // corner everyone is aiming for is bottom-left.
  const sy = (v: number) => plotB - ((v - yMin) / (yMax - yMin)) * (plotB - plotT);

  // --- Build SVG content. We assemble as an HTML-ish string for simplicity
  // and assign with innerHTML; SVG elements created that way still get the
  // SVG namespace correctly when set on an existing <svg> root. Listeners
  // for hover are attached after assignment by querying the DOM.
  svg.setAttribute("viewBox", `0 0 ${PLOT_W} ${PLOT_H}`);

  const parts: string[] = [];

  // --- The ruled sheet ---------------------------------------------------
  // Two nested patterns: the fine one tiles inside the heavy one, so a
  // single <rect> fill produces both weights. Cells are square again —
  // 560/14 and 400/10 are both 40px — which is the whole reason the axis
  // planner works the way it does. patternTransform anchors the tiling to
  // the plot corner, which is itself a major rule.
  parts.push(`<defs>
    <pattern id="eng-minor" width="${cellW / MINOR_PER_MAJOR}" height="${cellH / MINOR_PER_MAJOR}" patternUnits="userSpaceOnUse">
      <path class="rule-minor" d="M ${cellW / MINOR_PER_MAJOR} 0 L 0 0 0 ${cellH / MINOR_PER_MAJOR}"/>
    </pattern>
    <pattern id="eng-major" width="${cellW}" height="${cellH}" patternUnits="userSpaceOnUse"
             patternTransform="translate(${plotL} ${plotT})">
      <rect width="${cellW}" height="${cellH}" fill="url(#eng-minor)"/>
      <path class="rule-major" d="M ${cellW} 0 L 0 0 0 ${cellH}"/>
    </pattern>
  </defs>`);

  // Bleed lines: major rules only, continued out of the frame to the edge
  // of the sheet. Drawn first so the tick labels and the title block sit
  // over them — on a real pad the ruling is printed and everything else is
  // written on top of it. The minor rules deliberately stop at the frame;
  // running all of them out would turn the margins into mush.
  const bleed: string[] = [];
  for (let j = 0; j <= GRID_COLS; j++) {
    const x = plotL + j * cellW;
    bleed.push(`M ${x} 0 V ${plotT}`);
    bleed.push(`M ${x} ${plotB} V ${PLOT_H}`);
  }
  for (let i = 0; i <= GRID_ROWS; i++) {
    const y = plotT + i * cellH;
    bleed.push(`M 0 ${y} H ${plotL}`);
    bleed.push(`M ${plotR} ${y} H ${PLOT_W}`);
  }
  parts.push(`<path class="rule-bleed" d="${bleed.join(" ")}"/>`);

  // Ruled plot area, then a double frame: heavy rule at the plot bounds,
  // hairline 5px outside it.
  parts.push(`<rect x="${plotL}" y="${plotT}" width="${plotR - plotL}" height="${plotB - plotT}"
    fill="url(#eng-major)"/>`);
  parts.push(`<rect class="frame-inner" x="${plotL}" y="${plotT}"
    width="${plotR - plotL}" height="${plotB - plotT}"/>`);
  parts.push(`<rect class="frame-outer" x="${plotL - 5}" y="${plotT - 5}"
    width="${plotR - plotL + 10}" height="${plotB - plotT + 10}"/>`);

  // Tick marks and labels. The grid already carries the position, so these
  // are short marks stepping outside the frame rather than full rules.
  const tickPath: string[] = [];
  for (const tv of xTicks) {
    const x = sx(tv);
    tickPath.push(`M ${x} ${plotB} v 6`);
    parts.push(`<text class="axis-text" x="${x}" y="${plotB + 18}"
      text-anchor="middle">${tv.toFixed(xDp)}</text>`);
  }
  for (const tv of yTicks) {
    const y = sy(tv);
    tickPath.push(`M ${plotL} ${y} h -6`);
    parts.push(`<text class="axis-text" x="${plotL - 11}" y="${y + 4}"
      text-anchor="end">${tv.toFixed(yDp)}</text>`);
  }
  parts.push(`<path class="tick-mark" d="${tickPath.join(" ")}"/>`);

  // Axis captions. The x caption sits left of the title block, so its
  // centre is the midpoint of the space actually left over, not of the
  // plot — otherwise it drifts under the block on wide sheets.
  parts.push(`<text class="axis-title" x="${(plotL + 400) / 2}" y="${plotB + 40}"
    text-anchor="middle">COST (M$) →</text>`);
  parts.push(`<text class="axis-title" x="18" y="${(plotT + plotB) / 2}"
    text-anchor="middle" transform="rotate(-90 18 ${(plotT + plotB) / 2})">TIME (min) →</text>`);

  // Title block, far right of the band. Opaque fill so it reads as a
  // printed box sitting on the sheet rather than floating over the ruling
  // that bleeds underneath it.
  const tbX = 408, tbY = plotB + 22, tbW = PLOT_W - 16 - tbX, tbH = 30;
  const tbSplit = tbX + 108;
  parts.push(
    `<g>` +
    `<rect class="sheet-fill" x="${tbX}" y="${tbY}" width="${tbW}" height="${tbH}"/>` +
    `<rect class="frame-inner" x="${tbX}" y="${tbY}" width="${tbW}" height="${tbH}"/>` +
    `<path class="block-rule" stroke-width="0.6" d="M ${tbSplit} ${tbY} v ${tbH}"/>` +
    `<text class="block-text" font-size="9" x="${tbX + 7}" y="${tbY + 12}">SHEET</text>` +
    `<text class="block-text" font-size="11" x="${tbX + 7}" y="${tbY + 25}">${dateStr ? escapeHtml(dateStr) : "COST × TIME"}</text>` +
    `<text class="block-text" font-size="9" x="${tbSplit + 7}" y="${tbY + 12}">ENTRIES</text>` +
    `<text class="block-text" font-size="11" x="${tbSplit + 7}" y="${tbY + 25}">n = ${subs.length}</text>` +
    `</g>`
  );

  // Pareto frontier line: stepped (better-than dominance is monotone in
  // both axes), connecting frontier points in cost-ascending order.
  if (frontierSeq.length >= 2) {
    let d = `M ${sx(frontierSeq[0].cost)} ${sy(frontierSeq[0].timeMin)}`;
    for (let i = 1; i < frontierSeq.length; i++) {
      const px = sx(frontierSeq[i].cost), py = sy(frontierSeq[i].timeMin);
      const prevY = sy(frontierSeq[i - 1].timeMin);
      // Step: horizontal first, then down (cost increases, time decreases).
      d += ` L ${px} ${prevY} L ${px} ${py}`;
    }
    parts.push(`<path d="${d}" fill="none" stroke="#325a32" stroke-width="1.5"
      stroke-dasharray="2 3" stroke-opacity="0.65"/>`);
  }

  // Points. Drawn in order so "current" sits on top of everything else
  // (it's the dot the player is actively manipulating).
  const styleFor = (kind: Pt["kind"]) => {
    switch (kind) {
      case "existing": return { fill: "#a04030", stroke: "#2a3a2e", r: 6, shape: "diamond" as const };
      case "current":  return { fill: "none",    stroke: "#c44a30", r: 8, shape: "crosshair" as const };
      case "you":      return { fill: "#c44a30", stroke: "#2a3a2e", r: 6, shape: "circle" as const };
      case "pareto":   return { fill: "#325a32", stroke: "#2a3a2e", r: 5, shape: "circle" as const };
      default:         return { fill: "#4a5a4e", stroke: "#2a3a2e", r: 3.5, shape: "circle" as const };
    }
  };
  // Sort so most-important kinds render last (on top). "current" comes
  // dead last — it's the live dot the player is watching.
  const order: Pt["kind"][] = ["norm", "pareto", "you", "existing", "current"];
  const ordered = points.slice().sort((a, b) => order.indexOf(a.kind) - order.indexOf(b.kind));
  for (let i = 0; i < ordered.length; i++) {
    const p = ordered[i];
    const s = styleFor(p.kind);
    const cx = sx(p.x), cy = sy(p.y);
    const attrs = `data-cost="${p.cost}" data-time="${p.timeMin}" data-label="${escapeHtml(p.label)}"
      class="plot-point" style="cursor: crosshair;"`;
    if (s.shape === "diamond") {
      // Diamond via rotated square. Side ≈ 2*r/√2.
      const half = s.r;
      parts.push(`<rect x="${cx - half}" y="${cy - half}" width="${half * 2}" height="${half * 2}"
        fill="${s.fill}" stroke="${s.stroke}" stroke-width="1"
        transform="rotate(45 ${cx} ${cy})" ${attrs}/>`);
    } else if (s.shape === "crosshair") {
      // Ring + center dot + two short ticks for a "live target" marker.
      // The wrapping <g> carries the data-* attrs so hover still works.
      const tick = s.r * 0.55;
      // Invisible hit disc covering the marker's full extent, ticks
      // included. Without it the only hoverable pixels are the painted
      // strokes — a 1.75px ring you have to trace to get a tooltip. It
      // needs pointer-events="all" rather than a transparent fill:
      // fill="none" is skipped by hit-testing entirely, and a real fill
      // would occlude points sitting behind this one. Listed first so it
      // sits under the visible strokes; hit-testing still finds it
      // everywhere they don't paint.
      parts.push(
        `<g ${attrs}>` +
        `<circle cx="${cx}" cy="${cy}" r="${s.r + tick}" fill="none" pointer-events="all"/>` +
        `<circle cx="${cx}" cy="${cy}" r="${s.r}" fill="none" stroke="${s.stroke}" stroke-width="1.75"/>` +
        `<circle cx="${cx}" cy="${cy}" r="1.5" fill="${s.stroke}" stroke="none"/>` +
        `<line x1="${cx - s.r - tick}" y1="${cy}" x2="${cx - s.r + 1}" y2="${cy}" stroke="${s.stroke}" stroke-width="1.5"/>` +
        `<line x1="${cx + s.r - 1}" y1="${cy}" x2="${cx + s.r + tick}" y2="${cy}" stroke="${s.stroke}" stroke-width="1.5"/>` +
        `<line x1="${cx}" y1="${cy - s.r - tick}" x2="${cx}" y2="${cy - s.r + 1}" stroke="${s.stroke}" stroke-width="1.5"/>` +
        `<line x1="${cx}" y1="${cy + s.r - 1}" x2="${cx}" y2="${cy + s.r + tick}" stroke="${s.stroke}" stroke-width="1.5"/>` +
        `</g>`
      );
    } else {
      parts.push(`<circle cx="${cx}" cy="${cy}" r="${s.r}"
        fill="${s.fill}" stroke="${s.stroke}" stroke-width="1" ${attrs}/>`);
    }
  }

  svg.innerHTML = parts.join("");

  // Hover handling: a single delegated listener on the SVG reads data-*
  // attributes from the target. Tooltip is positioned in the modal-body's
  // coordinate space using the SVG's clientRect to convert.
  const tooltip = document.getElementById("plot-tooltip")!;
  const body = svg.parentElement!;
  svg.onmousemove = (ev: MouseEvent) => {
    // The "crosshair" current-road marker is a <g> with children — events
    // land on the leaf <circle>/<line>, not the <g>. Walk up to find the
    // .plot-point ancestor (works for simple circles too: the circle is
    // itself the .plot-point).
    let t: Element | null = ev.target as Element | null;
    while (t && t !== svg && !t.classList?.contains("plot-point")) {
      t = t.parentElement;
    }
    if (!t || t === svg || !t.classList?.contains("plot-point")) {
      tooltip.setAttribute("hidden", "");
      return;
    }
    const label = t.getAttribute("data-label") ?? "";
    const cost = parseFloat(t.getAttribute("data-cost") ?? "0");
    const time = parseFloat(t.getAttribute("data-time") ?? "0");
    tooltip.innerHTML =
      `<div class="pt-handle">${label}</div>` +
      `<div class="pt-stats">${cost.toFixed(1)} M$ · ${time.toFixed(1)} min</div>`;
    tooltip.removeAttribute("hidden");
    // Position above-and-right of the cursor, clamped to the modal body.
    const bodyRect = body.getBoundingClientRect();
    let tx = ev.clientX - bodyRect.left + 12;
    let ty = ev.clientY - bodyRect.top - 28;
    const ttRect = tooltip.getBoundingClientRect();
    if (tx + ttRect.width > bodyRect.width) tx = ev.clientX - bodyRect.left - ttRect.width - 12;
    if (ty < 0) ty = ev.clientY - bodyRect.top + 16;
    tooltip.style.left = `${tx}px`;
    tooltip.style.top = `${ty}px`;
  };
  svg.onmouseleave = () => tooltip.setAttribute("hidden", "");
}

function openPlot() {
  const modal = document.getElementById("plot-modal")!;
  modal.removeAttribute("hidden");
  renderScatterplot();
}
function closePlot() {
  const modal = document.getElementById("plot-modal")!;
  modal.setAttribute("hidden", "");
  // Clear tooltip too, in case it was visible at close time.
  document.getElementById("plot-tooltip")?.setAttribute("hidden", "");
}

// =============================================================
// BUTTONS & INIT
// =============================================================

document.getElementById("btn-reset")!.addEventListener("click", () => {
  // Reset is undoable: it discards everything, which is exactly the action
  // players most want back after a misclick.
  if (puzzle) { withUndo(() => { road = defaultRoad(puzzle!); }); draw(); }
});
document.getElementById("btn-recenter")!.addEventListener("click", () => {
  viewScale = 1; viewPanX = 0; viewPanY = 0;
  // Reseeds the directional baseline; at fit zoom (0, 0) is already neutral,
  // so this leaves the position alone and only resets what the next pan
  // measures against.
  clampPanTo();
  commitNavigating(); invalidateStatic(); draw();
});
document.getElementById("btn-add")!.addEventListener("click", () => { withUndo(() => addSegment()); draw(); });
document.getElementById("btn-remove")!.addEventListener("click", () => { withUndo(() => removeSegmentMid()); draw(); });
document.getElementById("btn-debug")!.addEventListener("click", (e) => {
  DEBUG_GRADE_VIZ = !DEBUG_GRADE_VIZ;
  (e.target as HTMLButtonElement).textContent = DEBUG_GRADE_VIZ ? "Debug: ON" : "Debug: Grade";
  draw();
});
document.getElementById("btn-cars")!.addEventListener("click", (e) => {
  SHOW_CARS = !SHOW_CARS;
  (e.target as HTMLButtonElement).textContent = SHOW_CARS ? "Cars: ON" : "Cars: OFF";
  if (SHOW_CARS) ensureAnimating();
  else redraw();   // one final redraw to clear cars
});
document.getElementById("btn-submit")!.addEventListener("click", submitScore);

// Scatterplot modal: button opens, X button / backdrop click / Esc closes.
document.getElementById("btn-plot")!.addEventListener("click", openPlot);
document.getElementById("btn-plot-close")!.addEventListener("click", closePlot);
document.getElementById("plot-modal")!.addEventListener("click", (e) => {
  // Close only when the click lands on the backdrop itself, not inside
  // the panel — the modal-backdrop is the event target only when the
  // user clicked outside the panel.
  if (e.target === e.currentTarget) closePlot();
});
// ESC closes the plot modal. Registered separately from the debug-key
// handler below so it fires regardless of focus (the debug shortcuts skip
// when the handle input is focused, but ESC should always dismiss).
window.addEventListener("keydown", (e) => {
  if (e.key !== "Escape") return;
  const modal = document.getElementById("plot-modal");
  if (modal && !modal.hasAttribute("hidden")) {
    e.preventDefault();
    closePlot();
  }
});

// Win modal: four ways out — the × in the header, the button at the foot, a
// backdrop click, and Escape. It has no open button; renderTarget opens it.
document.getElementById("win-modal-close")!.addEventListener("click", closeWinModal);
document.getElementById("win-modal-x")!.addEventListener("click", closeWinModal);
document.getElementById("win-modal")!.addEventListener("click", (e) => {
  if (e.target === e.currentTarget) closeWinModal();
});
window.addEventListener("keydown", (e) => {
  if (e.key !== "Escape") return;
  const modal = document.getElementById("win-modal");
  if (modal && !modal.hasAttribute("hidden")) {
    e.preventDefault();
    closeWinModal();
  }
});

// Undo: Ctrl+Z, and Cmd+Z on macOS.
//
// A SEPARATE listener from the debug shortcuts below, which bail on any
// modifier key (`if (e.ctrlKey || e.metaKey || e.altKey) return;`) — adding
// Ctrl+Z as another branch there would never fire. Registered first so undo
// stays available regardless of what gets added to that handler later.
window.addEventListener("keydown", (e) => {
  const target = e.target as HTMLElement | null;
  if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;
  if (e.key !== "z" && e.key !== "Z") return;
  if (!e.ctrlKey && !e.metaKey) return;
  if (e.altKey) return;
  // Ctrl+Shift+Z is the conventional redo. Not implemented, but swallowing it
  // here would be worse than ignoring it: the browser's own default is at
  // least predictable, and a redo added later slots straight in.
  if (e.shiftKey) return;
  // Stop the browser applying its own undo to any focused field.
  e.preventDefault();
  undo();
});

// Keyboard shortcuts (developer-only). Skip if the user is typing in an
// input field — the handle entry would otherwise eat your "e" and "s".
window.addEventListener("keydown", (e) => {
  const target = e.target as HTMLElement | null;
  if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  if (e.key === "s" || e.key === "S") {
    DEBUG_SKELETON = !DEBUG_SKELETON;
    console.log(`Debug: skeleton ${DEBUG_SKELETON ? "ON" : "OFF"}`);
    redraw();
  } else if (e.key === "e" || e.key === "E") {
    DEBUG_ELEVATION = !DEBUG_ELEVATION;
    console.log(`Debug: elevation probe ${DEBUG_ELEVATION ? "ON (click to probe)" : "OFF"}`);
    redraw();
  } else if (e.key === "c" || e.key === "C") {
    if (elevationProbes.length > 0) {
      elevationProbes = [];
      console.log("Debug: elevation probes cleared");
      redraw();
    }
  } else if (e.key === "d" || e.key === "D") {
    if (drainageCells.length === 0) {
      console.log("Debug: drainage data not loaded — reload with ?debug=true");
      return;
    }
    DEBUG_DRAINAGE = !DEBUG_DRAINAGE;
    console.log(`Debug: drainage ${DEBUG_DRAINAGE ? "ON" : "OFF"}`);
    redraw();
  } else if (e.key === "f" || e.key === "F") {
    withUndo(() => snapToContour());
  } else if (e.key === "[" || e.key === "]") {
    stepPuzzleDate(e.key === "[" ? -1 : 1);
  } else if (e.key === "g" || e.key === "G") {
    DEBUG_GRID = !DEBUG_GRID;
    console.log(`Debug: grid ${DEBUG_GRID ? "ON" : "OFF"}`);
    // The grid is baked into the static composite (unlike the skeleton/
    // drainage overlays, which are per-frame), so we must rebuild it —
    // a bare redraw() would just re-blit the cached composite and the
    // toggle wouldn't show until the next pan/zoom.
    invalidateStatic();
    redraw();
  }
});

// Tap on the mobile strip toggles the bottom-sheet panel. Used only on
// narrow viewports — on desktop the strip is hidden via CSS so this never
// fires.
document.getElementById("mobile-strip")!.addEventListener("click", () => {
  document.body.classList.toggle("sheet-expanded");
});

// =============================================================
// ELEVATION PROFILE (strip below the map)
// =============================================================
//
// A pure VIEW over lastAnalysis — no new sampling. Reuses:
//   samples[i]      road points; .s = arc length in km (scoring convention)
//   z[i]            road elevation in km at sample i (drives grade in scoring)
//   kinds[i]        structure of segment i..i+1 (kinds.length = samples.length-1)
//   findStructureRuns(kinds)   contiguous runs of one structure kind
// Terrain under the road is heightAt(sample) * HEIGHT_SCALE_KM, the same
// conversion scoring uses. Bridges and tunnels DISPLAY as straight decks/
// bores between their endpoints (portal to portal, abutment to abutment):
// we lerp z across each run by arc length rather than plotting per-sample z.
// X and Y scale independently (classic engineering-profile vertical
// exaggeration); the V.E. badge states the factor.

const profileCanvas = document.getElementById("profile") as HTMLCanvasElement | null;
const profileCtx = profileCanvas ? profileCanvas.getContext("2d") : null;
let profCssW = 0, profCssH = 0;

// Layout shared between drawProfile and the double-click navigation handler
// (the handler inverts the x mapping: pixel -> arc length -> world point).
const PROF_PADL = 46, PROF_PADR = 10, PROF_PADT = 12, PROF_PADB = 16;
// Written by drawProfile each render; read by the dblclick handler.
// s0/s1 are the arc-length bounds of the currently displayed window.
let profGeom: { plotW: number; s0: number; s1: number } | null = null;

function resizeProfile() {
  if (!profileCanvas || !profileCtx) return;
  const rect = profileCanvas.getBoundingClientRect();
  if (rect.width < 40 || rect.height < 40) return;
  profCssW = rect.width; profCssH = rect.height;
  const dpr = window.devicePixelRatio || 1;
  profileCanvas.width = Math.round(profCssW * dpr);
  profileCanvas.height = Math.round(profCssH * dpr);
  profileCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  drawProfile();
}

/** 1/2/5-series step so ~targetTicks gridlines land on round values. */
function niceStep(range: number, targetTicks: number): number {
  if (range <= 0) return 1;
  const raw = range / targetTicks;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  for (const m of [1, 2, 5, 10]) if (raw <= m * mag) return m * mag;
  return 10 * mag;
}

function drawProfile() {
  if (!profileCanvas || !profileCtx || profCssW === 0) return;
  const pc = profileCtx;
  pc.clearRect(0, 0, profCssW, profCssH);
  if (!puzzle || !lastAnalysis) return;
  const { samples, z, kinds, score, deckSpans } = lastAnalysis;
  if (!samples || samples.length < 2) return;

  const n = samples.length;
  const totalKm = samples[n - 1].s;
  if (!(totalKm > 0)) return;

  // --- Domain: the stretch of road currently visible on the map ----------
  // The strip is a detail view of what the player is working on. Zoomed
  // out, every sample is on-screen and the domain is the whole road; zoomed
  // in it narrows to the visible span (plus a little context padding), and
  // the vertical scale adapts to LOCAL relief. If the road is entirely
  // off-screen (panned away), fall back to the full road so the strip stays
  // a navigation surface (dbl-click to jump back).
  let sMin = Infinity, sMax = -Infinity;
  for (let i = 0; i < n; i++) {
    const sp = toScreen(samples[i]);
    if (sp.x >= 0 && sp.x <= cssW && sp.y >= 0 && sp.y <= cssH) {
      if (samples[i].s < sMin) sMin = samples[i].s;
      if (samples[i].s > sMax) sMax = samples[i].s;
    }
  }
  let s0: number, s1: number;
  if (!(sMin < sMax)) {
    s0 = 0; s1 = totalKm;
  } else {
    const pad = (sMax - sMin) * 0.04 + 0.05;
    s0 = Math.max(0, sMin - pad);
    s1 = Math.min(totalKm, sMax + pad);
  }
  // Guard degenerate windows (e.g. one sample visible at extreme zoom).
  if (s1 - s0 < 0.5) {
    const mid = (s0 + s1) / 2;
    s0 = Math.max(0, mid - 0.25);
    s1 = Math.min(totalKm, mid + 0.25);
  }
  const spanKm = s1 - s0;

  // Sample index range covering [s0, s1] (inclusive, slightly generous so
  // curves reach the plot edges).
  let iLo = 0; while (iLo < n - 1 && samples[iLo + 1].s < s0) iLo++;
  let iHi = n - 1; while (iHi > 0 && samples[iHi - 1].s > s1) iHi--;
  if (iHi - iLo < 1) return;

  // --- Curves in km ------------------------------------------------------
  const terr = new Array<number>(n);
  for (let i = iLo; i <= iHi; i++) {
    terr[i] = heightAt(samples[i].x, samples[i].y, puzzle.params) * HEIGHT_SCALE_KM;
  }
  // Display z: per-sample road elevation, but linear across bridge/tunnel
  // runs (deck from abutment to abutment, bore from portal to portal).
  // Lerp uses the FULL run endpoints even when a portal sits outside the
  // window, so a partially visible deck still draws at its true elevation.
  const zDisp = z.slice(0, n);
  const runs = findStructureRuns(kinds);
  // Bridge decks come from the analysis as explicit spans now. Inferring
  // them here (chord from a run's first sample to its last) is what drew
  // decks starting underground: a segment is labelled "bridge" from its MEAN
  // deviation, so a run can begin at a sample that is itself far below the
  // terrain. bridgeDeckSpans puts the abutment at the ground crossing inside
  // that segment instead, and splits any span whose chord clips a rise.
  for (const sp of deckSpans) {
    const ds = sp.s1 - sp.s0;
    if (!(ds > 0)) continue;
    for (let i = 0; i < n; i++) {
      const si = samples[i].s;
      if (si < sp.s0 || si > sp.s1) continue;
      zDisp[i] = sp.z0 + (sp.z1 - sp.z0) * ((si - sp.s0) / ds);
    }
  }
  // Tunnels keep the old treatment: a bore runs straight portal to portal,
  // and a buried run has no equivalent of an abutment to misplace.
  for (const run of runs) {
    if (run.kind !== "tunnel") continue;
    const a = run.startIdx, b = run.endIdx;
    const sA = samples[a].s, sB = samples[b].s;
    const zA = z[a], zB = z[b];
    const ds = sB - sA;
    if (!(ds > 0)) continue;
    for (let i = a + 1; i < b; i++) {
      zDisp[i] = zA + (zB - zA) * ((samples[i].s - sA) / ds);
    }
  }

  // --- Scales ------------------------------------------------------------
  const PADL = PROF_PADL, PADR = PROF_PADR, PADT = PROF_PADT, PADB = PROF_PADB;
  const plotW = profCssW - PADL - PADR;
  const plotH = profCssH - PADT - PADB;
  if (plotW < 20 || plotH < 20) return;
  profGeom = { plotW, s0, s1 };

  let loKm = Infinity, hiKm = -Infinity;
  for (let i = iLo; i <= iHi; i++) {
    const t = terr[i], r = zDisp[i];
    if (t < loKm) loKm = t; if (t > hiKm) hiKm = t;
    if (r < loKm) loKm = r; if (r > hiKm) hiKm = r;
  }
  const waterKm = WATER_THRESHOLD * HEIGHT_SCALE_KM;
  let hasWater = false;
  for (let i = iLo; i <= iHi; i++) if (terr[i] < waterKm) { hasWater = true; break; }
  if (hasWater) { if (waterKm < loKm) loKm = waterKm; if (waterKm > hiKm) hiKm = waterKm; }
  let span = hiKm - loKm;
  if (span < 0.15) { const mid = (hiKm + loKm) / 2; loKm = mid - 0.075; hiKm = mid + 0.075; span = 0.15; }
  loKm -= span * 0.10; hiKm += span * 0.08;

  const sx = (s: number) => PADL + ((s - s0) / spanKm) * plotW;
  const sy = (km: number) => PADT + (1 - (km - loKm) / (hiKm - loKm)) * plotH;

  // --- Grid + axis labels (absolute km along the route, so labels keep
  //     their meaning as the window moves) --------------------------------
  pc.save();
  pc.beginPath(); pc.rect(PADL, 0, plotW, profCssH); pc.clip();

  pc.font = "9px 'Courier New', monospace";
  pc.strokeStyle = "rgba(74, 90, 78, 0.22)"; pc.lineWidth = 1; pc.setLineDash([]);
  pc.fillStyle = "#4a5a4e";
  const xStep = niceStep(spanKm, 8);
  for (let v = Math.ceil(s0 / xStep) * xStep; v <= s1 + 1e-9; v += xStep) {
    const x = sx(v);
    pc.beginPath(); pc.moveTo(x, PADT); pc.lineTo(x, PADT + plotH); pc.stroke();
    pc.textAlign = "center"; pc.textBaseline = "top";
    pc.fillText(`${Math.round(v * 100) / 100}`, x, PADT + plotH + 3);
  }
  pc.textAlign = "left";
  pc.fillText("km", PADL + plotW - 16, PADT + plotH + 3);

  // --- Terrain silhouette (opaque so grid reads as "behind ground") ------
  pc.beginPath();
  pc.moveTo(sx(samples[iLo].s), sy(terr[iLo]));
  for (let i = iLo + 1; i <= iHi; i++) pc.lineTo(sx(samples[i].s), sy(terr[i]));
  pc.lineTo(sx(samples[iHi].s), PADT + plotH);
  pc.lineTo(sx(samples[iLo].s), PADT + plotH);
  pc.closePath();
  pc.fillStyle = "#d9d0b2";
  pc.fill();
  pc.strokeStyle = "rgba(42, 58, 46, 0.75)"; pc.lineWidth = 1.25;
  pc.beginPath();
  pc.moveTo(sx(samples[iLo].s), sy(terr[iLo]));
  for (let i = iLo + 1; i <= iHi; i++) pc.lineTo(sx(samples[i].s), sy(terr[i]));
  pc.stroke();

  // --- Water: tint everything below the waterline ------------------------
  if (hasWater) {
    const wy = sy(waterKm);
    pc.fillStyle = "rgba(90, 120, 150, 0.25)";
    pc.fillRect(PADL, wy, plotW, PADT + plotH - wy);
    pc.strokeStyle = "rgba(90, 120, 150, 0.8)"; pc.lineWidth = 1; pc.setLineDash([5, 4]);
    pc.beginPath(); pc.moveTo(PADL, wy); pc.lineTo(PADL + plotW, wy); pc.stroke();
    pc.setLineDash([]);
  }

  // --- Bridge deck ticks (under the road line) ---------------------------
  for (const run of runs) {
    if (run.kind !== "bridge") continue;
    const a = Math.max(run.startIdx, iLo), b = Math.min(run.endIdx, iHi);
    if (a > b) continue;
    pc.strokeStyle = "rgba(120, 90, 30, 0.9)"; pc.lineWidth = 1;
    for (let i = a; i <= b; i++) {
      const x = sx(samples[i].s);
      pc.beginPath(); pc.moveTo(x, sy(zDisp[i])); pc.lineTo(x, sy(terr[i])); pc.stroke();
    }
  }

  // --- Road line per structure run (map's color language) ----------------
  const styleFor = (kind: Kind): { color: string; width: number; dash: number[] } => {
    switch (kind) {
      case "tunnel":  return { color: "rgba(60, 60, 60, 0.95)",  width: 2.5, dash: [7, 5] };
      case "opencut": return { color: "rgba(60, 130, 60, 0.95)", width: 2.5, dash: [4, 3] };
      // Embankment: an at-grade road standing on placed earth, so it reads
      // as a heavier version of the at-grade line rather than a structure.
      case "fill":    return { color: "rgba(120, 96, 40, 0.95)", width: 3,   dash: [] };
      case "bridge":  return { color: "rgba(200, 168, 56, 1.0)", width: 3,   dash: [] };
      default:        return { color: "rgba(26, 74, 26, 0.95)",  width: 2.5, dash: [] };
    }
  };
  pc.lineCap = "butt"; pc.lineJoin = "round";
  for (const run of runs) {
    const a = Math.max(run.startIdx, iLo), b = Math.min(run.endIdx, iHi);
    if (a >= b) continue;
    const st = styleFor(run.kind);
    pc.strokeStyle = st.color; pc.lineWidth = st.width; pc.setLineDash(st.dash);
    pc.beginPath();
    pc.moveTo(sx(samples[a].s), sy(zDisp[a]));
    for (let i = a + 1; i <= b; i++) pc.lineTo(sx(samples[i].s), sy(zDisp[i]));
    pc.stroke();
  }
  pc.setLineDash([]);

  // --- Portal / abutment markers (only those inside the window) ----------
  for (const run of runs) {
    if (run.kind !== "bridge" && run.kind !== "tunnel") continue;
    const color = run.kind === "bridge" ? "rgba(120, 90, 30, 1)" : "rgba(60, 60, 60, 1)";
    pc.strokeStyle = color; pc.lineWidth = 2;
    for (const i of [run.startIdx, run.endIdx]) {
      if (i < iLo || i > iHi) continue;
      const x = sx(samples[i].s), y = sy(zDisp[i]);
      pc.beginPath(); pc.moveTo(x, y - 4); pc.lineTo(x, y + 4); pc.stroke();
    }
  }

  // --- Per-structure cost labels -----------------------------------------
  // Analysis exposes only per-kind cost TOTALS (score.costBridge/costTunnel),
  // so each run's share is allocated proportional to its length. This is
  // exact when the per-km rate is uniform within a kind; if scoring prices
  // height/depth per-sample it's an approximation — but runs always sum to
  // the panel total, so the books balance.
  {
    const runLen = (r: { startIdx: number; endIdx: number }) =>
      samples[r.endIdx].s - samples[r.startIdx].s;
    const lenSum = { bridge: 0, tunnel: 0 } as Record<string, number>;
    for (const run of runs) {
      if (run.kind === "bridge" || run.kind === "tunnel") lenSum[run.kind] += runLen(run);
    }
    pc.font = "9px 'Courier New', monospace";
    pc.textAlign = "center";
    for (const run of runs) {
      if (run.kind !== "bridge" && run.kind !== "tunnel") continue;
      const a = Math.max(run.startIdx, iLo), b = Math.min(run.endIdx, iHi);
      if (a >= b) continue;
      const total = run.kind === "bridge" ? score.costBridge : score.costTunnel;
      const denom = lenSum[run.kind];
      if (!(denom > 0) || !Number.isFinite(total)) continue;
      const cost = total * (runLen(run) / denom);
      // Skip labels that wouldn't fit over the run's visible extent.
      if (sx(samples[b].s) - sx(samples[a].s) < 34) continue;
      // Anchor at the midpoint of the VISIBLE extent so partially windowed
      // structures keep their label on-screen. The deck/bore is linear in
      // zDisp, so its elevation there is a lerp between the true endpoints.
      const sMid = (samples[a].s + samples[b].s) / 2;
      const dsRun = samples[run.endIdx].s - samples[run.startIdx].s;
      const f = dsRun > 0 ? (sMid - samples[run.startIdx].s) / dsRun : 0;
      const yLine = sy(zDisp[run.startIdx] + (zDisp[run.endIdx] - zDisp[run.startIdx]) * f);
      const x = sx(sMid);
      const label = `${cost.toFixed(1)} M$`;
      if (run.kind === "bridge") {
        pc.fillStyle = "rgba(120, 90, 30, 1)";
        pc.textBaseline = "bottom";
        pc.fillText(label, x, Math.max(yLine - 6, PADT + 9));
      } else {
        pc.fillStyle = "rgba(60, 60, 60, 1)";
        pc.textBaseline = "top";
        pc.fillText(label, x, Math.min(yLine + 7, PADT + plotH - 10));
      }
    }
  }

  pc.restore();

  // --- Town labels: floated just above the profile line at their road end,
  //     with a thin grey leader arrow to the exact endpoint ---------------
  // Clearance: the asked-for check is the line height at the INNER end of
  // the textbox (terrain usually climbs inland); we take the highest line
  // point under the whole textbox, which includes that end and also stays
  // clear when the peak falls mid-label.
  pc.font = "9px 'Courier New', monospace";
  const labelTown = (name: string, side: "left" | "right") => {
    const endIdx = side === "left" ? iLo : iHi;
    const endX = sx(samples[endIdx].s);
    const endY = sy(terr[endIdx]);
    const text = name.toUpperCase();
    const tw = pc.measureText(text).width;
    const inset = 12;
    const xText = side === "left" ? PADL + inset : PADL + plotW - inset;
    const boxL = side === "left" ? xText : xText - tw;
    const boxR = side === "left" ? xText + tw : xText;
    let topY = endY;
    for (let i = iLo; i <= iHi; i++) {
      const x = sx(samples[i].s);
      if (x < boxL - 2 || x > boxR + 2) continue;
      const y = sy(terr[i]);
      if (y < topY) topY = y;
    }
    const yBase = Math.max(12, topY - 5);          // baseline a few px above the line
    pc.fillStyle = "#4a5a4e";
    pc.textAlign = side; pc.textBaseline = "bottom";
    pc.fillText(text, xText, yBase);
    // Leader arrow from the label's outer corner down to the road endpoint.
    const x0 = side === "left" ? boxL - 3 : boxR + 3;
    const y0 = yBase - 3;
    pc.strokeStyle = "rgba(74, 90, 78, 0.65)"; pc.lineWidth = 1; pc.setLineDash([]);
    pc.beginPath(); pc.moveTo(x0, y0); pc.lineTo(endX, endY - 1); pc.stroke();
    const ang = Math.atan2(endY - 1 - y0, endX - x0);
    pc.beginPath();
    pc.moveTo(endX, endY - 1);
    pc.lineTo(endX - 4 * Math.cos(ang - 0.4), endY - 1 - 4 * Math.sin(ang - 0.4));
    pc.moveTo(endX, endY - 1);
    pc.lineTo(endX - 4 * Math.cos(ang + 0.4), endY - 1 - 4 * Math.sin(ang + 0.4));
    pc.stroke();
  };
  if (puzzle.towns.length >= 2) {
    if (s0 <= 1e-6) labelTown(puzzle.towns[0].name, "left");
    if (s1 >= totalKm - 1e-6) labelTown(puzzle.towns[1].name, "right");
  }
  // Windowed-view badge: which part of the route is shown, plus V.E.
  const ve = (plotH / (hiKm - loKm)) / (plotW / spanKm);
  pc.textAlign = "right";
  const windowed = spanKm < totalKm - 1e-6;
  const veTxt = `V.E. ×${ve >= 10 ? Math.round(ve) : ve.toFixed(1)}`;
  pc.fillText(
    windowed ? `${s0.toFixed(1)}–${s1.toFixed(1)} of ${totalKm.toFixed(1)} km · ${veTxt}` : veTxt,
    PADL + plotW - 2, PADT + 2,
  );
}

if (profileCanvas) {
  // Fires on initial observation too, so this covers first paint AND any
  // layout-driven size change (window resize, mobile strip height, etc.).
  new ResizeObserver(() => resizeProfile()).observe(profileCanvas.parentElement ?? profileCanvas);

  // Double-click (double-tap): jump the MAP to the clicked spot on the road.
  // The profile stays a full-road overview; the map is the detail view, so
  // the jump also zooms in — but never zooms the user OUT if they're already
  // inspecting at higher magnification.
  const PROFILE_JUMP_ZOOM = 3;
  profileCanvas.addEventListener("dblclick", (e) => {
    if (!lastAnalysis || !puzzle || !profGeom) return;
    const { samples } = lastAnalysis;
    if (!samples || samples.length < 2) return;

    const rect = profileCanvas.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const frac = clamp((px - PROF_PADL) / profGeom.plotW, 0, 1);
    const s = profGeom.s0 + frac * (profGeom.s1 - profGeom.s0);

    // Binary search the (monotonic) arc-length table, then lerp between the
    // bracketing samples for a smooth world-space target.
    let lo = 0, hi = samples.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (samples[mid].s <= s) lo = mid; else hi = mid;
    }
    const a = samples[lo], b = samples[hi];
    const ds = b.s - a.s;
    const f = ds > 0 ? (s - a.s) / ds : 0;
    const w = { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };

    viewScale = clamp(Math.max(viewScale, PROFILE_JUMP_ZOOM), MIN_ZOOM, MAX_ZOOM);
    viewPanX = cssW / 2 - fitOffsetX - w.x * fitScale * viewScale;
    viewPanY = cssH / 2 - fitOffsetY - w.y * fitScale * viewScale;
    clampPanTo();
    // Discrete action: render at full quality immediately (same treatment
    // as recenter), then repaint the profile so the brackets track the jump.
    commitNavigating();
    invalidateStatic();
    scheduleRender();
    drawProfile();
  });
}

window.addEventListener("resize", resizeCanvas);

(async function init() {

  // Optional control; gives the player a one-click way to wipe the only
  // thing this game stores on their device.
  document.getElementById("btn-reset-streak")?.addEventListener("click", () => {
    if (confirm("Reset your frontier streak? This can't be undone.")) resetStreak();
  });

  let timer: ReturnType<typeof setInterval> | null = null;
  function startPollingLeaderboard() {
    if (timer !== null) return;
    refreshLeaderboard();                       // catch up immediately on return
    timer = setInterval(refreshLeaderboard, 30_000);
  }
  function stopPollingLeaderboard() {
    if (timer === null) return;
    clearInterval(timer);
    timer = null;
  }
  document.addEventListener('visibilitychange', () => {
    document.visibilityState === 'visible' ? startPollingLeaderboard() : stopPollingLeaderboard();
  });

  try {
    await fetchPuzzle();
    requestAnimationFrame(() => {
      resizeCanvas();
      requestAnimationFrame(() => {
        resizeCanvas();
        ensureAnimating();   // start the animation loop after first paint
      });
    });

    startPollingLeaderboard();
  } catch (err) {
    document.getElementById("puzzle-subtitle")!.textContent = "Failed to load puzzle";
    console.error(err);
  }
})();
